// BusinessUserProfileScreen.js — Full-featured business user profile with real API and offline support
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  Image,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Switch,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
  Share,
  Linking,

} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { storage } from '../../../utils/storage';
// Direct API config — no dependency on api service
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getHeaders = async (contentType = 'application/json') => {
  const tok = await AsyncStorage.getItem('auth_token')
            || await AsyncStorage.getItem('token') || '';
  return {
    ...(contentType ? { 'Content-Type': contentType } : {}),
    'X-API-Key':    API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const apiGet = async (path) => {
  const h = await getHeaders();
  const r = await fetch(`${API_BASE_URL}/api${path}`, { headers: h });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.message || `HTTP ${r.status}`);
  return d;
};

const apiPost = async (path, body) => {
  const h = await getHeaders();
  const r = await fetch(`${API_BASE_URL}/api${path}`, { method:'POST', headers:h, body:JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.message || `HTTP ${r.status}`);
  return d;
};

const apiPut = async (path, body) => {
  const h = await getHeaders();
  const r = await fetch(`${API_BASE_URL}/api${path}`, { method:'PUT', headers:h, body:JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.message || `HTTP ${r.status}`);
  return d;
};

const apiDelete = async (path) => {
  const h = await getHeaders();
  const r = await fetch(`${API_BASE_URL}/api${path}`, { method:'DELETE', headers:h });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || d.message || `HTTP ${r.status}`);
  return d;
};


// Upload profile image — correct endpoint: PUT /api/team/members/:id
const apiUploadImage = async (companyId, userId, imageUri, base64Data, email) => {
  const tok = await AsyncStorage.getItem('auth_token')
            || await AsyncStorage.getItem('token') || '';
  const authHeader = tok ? { Authorization: `Bearer ${tok}` } : {};
  const base64Uri = base64Data
    ? `data:image/jpeg;base64,${base64Data}`
    : imageUri;

  // Strategy 1: PUT /api/profile/update-image — email-based, works regardless of ID mapping
  const userEmail = email
    || await AsyncStorage.getItem('userEmail')
    || await AsyncStorage.getItem('user_email')
    || await AsyncStorage.getItem('email');

  if (userEmail) {
    try {
      const r = await fetch(`${API_BASE_URL}/api/profile/update-image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY, ...authHeader },
        body: JSON.stringify({ email: userEmail, profile_image: base64Uri, company_id: parseInt(companyId) || 0 }),
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        console.log('✅ Photo saved via email-based endpoint for', userEmail);
        return d.profile_image || base64Uri;
      }
      const errText = await r.text().catch(() => '');
      console.warn('Email-based upload failed:', r.status, errText);
    } catch (e) {
      console.warn('Email-based upload error:', e.message);
    }
  }

  // Strategy 2: PUT /api/team/members/:id using team member id
  try {
    const r = await fetch(`${API_BASE_URL}/api/team/members/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY, ...authHeader },
      body: JSON.stringify({ profile_image: base64Uri }),
    });
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      console.log('✅ Photo saved via PUT /api/team/members/:id');
      return d.data?.profile_image || base64Uri;
    }
    const errText = await r.text().catch(() => '');
    console.warn('PUT team/members failed:', r.status, errText);
  } catch (e) {
    console.warn('PUT team/members error:', e.message);
  }

  // Strategy 3: PUT /api/business-users/:userId
  try {
    const r = await fetch(`${API_BASE_URL}/api/business-users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY, ...authHeader },
      body: JSON.stringify({ profile_image: base64Uri, company_id: parseInt(companyId) || 0 }),
    });
    if (r.ok) {
      console.log('✅ Photo saved via PUT /api/business-users/:id');
      return base64Uri;
    }
  } catch (e) {
    console.warn('PUT business-users error:', e.message);
  }

  console.warn('⚠️ All upload strategies failed — using local cache only');
  return base64Uri;
};

const getCompanyId = async () => {
  const cid = await AsyncStorage.getItem('company_id')
            || await AsyncStorage.getItem('company_id');
  if (cid) return cid;
  try {
    const cp = await AsyncStorage.getItem('company_profile');
    if (cp) { const p = JSON.parse(cp); return p.id ? String(p.id) : null; }
  } catch {}
  return null;
};

const { width } = Dimensions.get('window');
const CACHE_KEY = 'business_user_data';

// ── Pure sub-components ────────────────────────────────────────────────────────
const Avatar = ({ uri, name, size = 72, onPress, editing }) => (
  <TouchableOpacity onPress={onPress} disabled={!onPress} activeOpacity={0.8}>
    {uri ? (
      <Image
        source={{ uri }}
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: '#E0E0E0',
        }}
      />
    ) : (
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: '#0e4e96',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Text style={{ color: '#fff', fontSize: size * 0.36, fontWeight: '800' }}>
          {name
            ? name
                .split(' ')
                .slice(0, 2)
                .map(w => w[0]?.toUpperCase())
                .join('')
            : '?'}
        </Text>
      </View>
    )}
    {editing && (
      <View
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 28,
          height: 28,
          borderRadius: 14,
          backgroundColor: '#0e4e96',
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 2,
          borderColor: '#fff',
        }}>
        <Ionicons name="camera" size={14} color="#fff" />
      </View>
    )}
  </TouchableOpacity>
);

const FormField = ({
  label,
  value,
  onChange,
  editing,
  placeholder,
  numeric,
  multiline,
  required,
  info,
}) => (
  <View style={{ marginBottom: 16 }}>
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6,
      }}>
      <Text style={styles.label}>
        {label}
        {required ? <Text style={{ color: '#D32F2F' }}> *</Text> : ''}
      </Text>
      {info ? <Text style={{ fontSize: 11, color: '#90A4AE' }}>{info}</Text> : null}
    </View>
    {editing ? (
      <TextInput
        style={[
          styles.input,
          multiline && { height: 78, textAlignVertical: 'top', paddingTop: 10 },
        ]}
        value={value || ''}
        onChangeText={onChange}
        placeholder={placeholder || label}
        placeholderTextColor="#BDBDBD"
        keyboardType={numeric ? 'decimal-pad' : 'default'}
        autoCorrect={false}
        multiline={multiline}
      />
    ) : (
      <Text style={styles.readValue} numberOfLines={multiline ? 4 : 1}>
        {value || '—'}
      </Text>
    )}
  </View>
);

const InfoRow = ({ icon, label, value, action, actionLabel, color }) => (
  <View style={styles.row}>
    <View style={[styles.rowIcon, { backgroundColor: (color || '#0e4e96') + '18' }]}>
      <MaterialCommunityIcons name={icon} size={17} color={color || '#0e4e96'} />
    </View>
    <View style={{ flex: 1 }}>
      <Text style={styles.rowLabel}>{label}</Text>
      {value ? <Text style={styles.rowValue}>{value}</Text> : null}
    </View>
    {action && (
      <TouchableOpacity
        onPress={action}
        style={{
          backgroundColor: '#E3F2FD',
          borderRadius: 12,
          paddingHorizontal: 12,
          paddingVertical: 5,
        }}>
        <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '700' }}>
          {actionLabel || 'Edit'}
        </Text>
      </TouchableOpacity>
    )}
  </View>
);

const ToggleSetting = ({ label, sub, value, onChange, disabled }) => (
  <View style={styles.toggleRow}>
    <View style={{ flex: 1, marginRight: 10 }}>
      <Text style={styles.toggleLabel}>{label}</Text>
      {sub && <Text style={styles.toggleSub}>{sub}</Text>}
    </View>
    <Switch
      value={!!value}
      onValueChange={onChange}
      disabled={disabled}
      trackColor={{ false: '#CFD8DC', true: '#0e4e96' }}
      thumbColor="#fff"
    />
  </View>
);

const SectionCard = ({ title, children, onAdd, addLabel }) => (
  <View style={styles.sectionCard}>
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
      }}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {onAdd && (
        <TouchableOpacity
          onPress={onAdd}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            backgroundColor: '#E3F2FD',
            borderRadius: 16,
            paddingHorizontal: 12,
            paddingVertical: 5,
          }}>
          <Ionicons name="add" size={16} color="#0e4e96" />
          <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '700' }}>
            {addLabel || 'Add'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
    {children}
  </View>
);

const Completeness = ({ pct }) => {
  const color = pct >= 90 ? '#388E3C' : pct >= 60 ? '#F57C00' : '#D32F2F';
  return (
    <View style={{ marginTop: 10 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
        <Text style={{ fontSize: 12, color: '#78909C', fontWeight: '600' }}>
          Profile Completeness
        </Text>
        <Text style={{ fontSize: 12, fontWeight: '800', color }}>{pct}%</Text>
      </View>
      <View style={{ height: 6, backgroundColor: '#ECEFF1', borderRadius: 3, overflow: 'hidden' }}>
        <View style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: 3 }} />
      </View>
    </View>
  );
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────────
export default function BusinessUserProfileScreen({ navigation, route }) {
  // Online state — use NetInfo if available, default to true
  const [isOnline, setIsOnline] = React.useState(true);
  React.useEffect(() => {
    let unsub;
    try {
      const NetInfo = require('@react-native-community/netinfo').default;
      NetInfo.fetch().then(s => setIsOnline(s.isConnected !== false));
      unsub = NetInfo.addEventListener(s => setIsOnline(s.isConnected !== false));
    } catch { setIsOnline(true); }
    return () => { try { unsub?.(); } catch {} };
  }, []);
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadingImg,  setUploadingImg]  = useState(false);
  const [exportingPdf,  setExportingPdf]  = useState(false);
  const [imgModal, setImgModal] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false);

  // Company integration
  const [companyId,      setCompanyId]      = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyTeam, setCompanyTeam]       = useState([]);     // all users for this company
  const [companyLoading, setCompanyLoading] = useState(false);

  // Skills state
  const [skills, setSkills] = useState([]);
  const [skillModal, setSkillModal] = useState(false);
  const [editSkill, setEditSkill] = useState(null);
  const [skillForm, setSkillForm] = useState({ name: '', proficiency: 2 });

  // Certifications state
  const [certs, setCerts] = useState([]);
  const [certModal, setCertModal] = useState(false);
  const [editCert, setEditCert] = useState(null);
  const [certForm, setCertForm] = useState({
    name: '',
    issuer: '',
    issue_date: '',
    expiry_date: '',
  });

  // Education state
  const [education, setEducation] = useState([]);
  const [eduModal, setEduModal] = useState(false);
  const [editEdu, setEditEdu] = useState(null);
  const [eduForm, setEduForm] = useState({
    institution: '',
    degree: '',
    field: '',
    start_year: '',
    end_year: '',
    grade: '',
  });

  // Experience state
  const [experience, setExperience] = useState([]);
  const [expModal, setExpModal] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [expForm, setExpForm] = useState({
    company: '',
    role: '',
    start_date: '',
    end_date: '',
    current: false,
    description: '',
  });

  // Password change
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [changingPw, setChangingPw] = useState(false);

  // ── Load user ID on mount ────────────────────────────────────────────────────
  useEffect(() => {
    const initializeUser = async () => {
      await resolveUserId();
    };
    initializeUser();
  }, []);

  useEffect(() => {
    if (userId) {
      console.log('👤 Loading profile for user ID:', userId);
      loadUserProfile();
      fetchCompanyProfile();
      fetchCompanyTeam();
    }
  }, [userId]);

  // Bootstrap company ID from storage — do NOT reset userId
  useEffect(() => {
    const bootstrapStorage = async () => {
      try {
        const cid = await getCompanyId();
        if (cid) setCompanyId(cid);
      } catch {}
    };
    bootstrapStorage();
  }, []);

  /**
   * Resolve the correct user ID by trying multiple sources in order
   */
  const resolveUserId = async () => {
    try {
      setLoading(true);

      // 1. Check route params first (most specific)
      if (route?.params?.userId) {
        const routeUserId = parseInt(route.params.userId);
        console.log('📱 Using user ID from route params:', routeUserId);
        setUserId(routeUserId);
        return;
      }

      // 2. Check AsyncStorage — user_id or user_data
      const storedId = await AsyncStorage.getItem('user_id');
      if (storedId) {
        const parsedId = parseInt(storedId);
        console.log('📱 Found stored user ID:', parsedId);
        if (!isNaN(parsedId) && parsedId > 0) {
          if (isOnline) {
            try {
              await apiGet(`/company/${await getCompanyId()}/team/${parsedId}`);
              setUserId(parsedId);
              return;
            } catch {
              // Not in team — still use the ID, profile may be in users table
              setUserId(parsedId);
              return;
            }
          } else {
            setUserId(parsedId);
            return;
          }
        }
      }

      // 3. Try to find by email if logged in
      const userEmail = await AsyncStorage.getItem('user_email');
      if (userEmail && isOnline) {
        try {
          const response = await apiGet(`/users?email=${encodeURIComponent(userEmail)}`);
          if (response.success && response.data && response.data.length > 0) {
            const foundUser = response.data[0];
            console.log('✅ Found user by email:', foundUser.id);
            await AsyncStorage.setItem('user_id', foundUser.id.toString());
            setUserId(foundUser.id);
            return;
          }
        } catch (error) {
          console.warn('Error finding user by email:', error);
        }
      }

      // 4. Try from user_data in AsyncStorage
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        const u = JSON.parse(userData);
        const uid = u.id || u.user_id;
        if (uid) {
          console.log('📱 User ID from user_data:', uid);
          await AsyncStorage.setItem('user_id', String(uid));
          setUserId(parseInt(uid));
          return;
        }
      }

      // 5. If all else fails, create a new user
      console.log('No existing user found, creating new profile');
      await createNewUser();
    } catch (error) {
      console.error('Error resolving user ID:', error);
      // Final fallback - try to use cached profile
      const cached = await loadCachedProfile('default');
      if (cached) {
        setProfile(cached);
        setOfflineMode(true);
        setLoading(false);
      } else {
        setUserNotFound(true);
        setLoading(false);
      }
    }
  };

  /**
   * Create a new user profile
   */
  const createNewUser = async () => {
    try {
      const userEmail = (await AsyncStorage.getItem('user_email')) || `user_${Date.now()}@jobeng.com`;
      const userName = (await AsyncStorage.getItem('user_name')) || 'New User';

      const newUser = {
        full_name: userName,
        email: userEmail,
        status: 'active',
        company_id: parseInt(await getCompanyId(), 10) || 0,
        created_by: parseInt(await AsyncStorage.getItem('user_id') || 1),
        preferences: {
          notifications: {
            email: true,
            push: true,
            sms: false,
            marketing: false,
            security_alerts: true,
            job_updates: true,
            team_updates: true,
          },
          privacy: {
            profile_visibility: 'team',
            contact_visibility: 'managers',
            activity_visibility: 'private',
            search_visibility: true,
            data_sharing: false,
          },
          security: {
            two_factor_enabled: false,
            biometric_enabled: false,
            session_timeout: 30,
            login_alerts: true,
            device_tracking: true,
          },
        },
      };

      if (isOnline) {
        const response = await apiPost('/company/' + (await getCompanyId()) + '/team', newUser);
        if (response.success && response.data) {
          const created = response.data;
          console.log('✅ New user created with ID:', created.id);
          if (created.id) await AsyncStorage.setItem('user_id', created.id.toString());
          if (created.id) setUserId(created.id);
          return;
        }
      }

      // Offline - create local user
      const tempId = Date.now();
      const tempUser = { ...newUser, id: tempId, user_id: `TEMP_${tempId}` };
      await cacheProfile(tempUser);
      setProfile(tempUser);
      setOfflineMode(true);
      setLoading(false);
    } catch (error) {
      console.error('Error creating user:', error);
      setUserNotFound(true);
      setLoading(false);
    }
  };

  // ── Load user profile ────────────────────────────────────────────────────────
  // Normalise team member / user record to profile shape
  const normaliseTeamMember = (m) => ({
    id:            m.id || m.user_id || userId,
    user_id:       m.user_id || m.id || userId,
    full_name:     m.display_name || m.full_name || m.name || '',
    email:         m.display_email || m.email || '',
    phone:         m.phone || '',
    job_title:     m.role || m.job_title || m.position || '',
    department:    m.department_name || m.department || '',
    profile_image: m.profile_image || m.avatar || null,
    status:        m.status || 'active',
    company_id:    m.company_id || companyId || null,
    is_admin:      m.is_admin || false,
    employee_id:   m.employee_id || m.id || null,
    bio:           m.bio || '',
    location:      m.location || '',
    linkedin:      m.linkedin || m.linkedin_url || '',
    skills:        m.skills || [],
    emergency_contact_name:         m.emergency_contact_name         || '',
    emergency_contact_phone:        m.emergency_contact_phone        || '',
    emergency_contact_email:        m.emergency_contact_email        || '',
    emergency_contact_address:      m.emergency_contact_address      || '',
    emergency_contact_rel:          m.emergency_contact_relationship || m.emergency_contact_rel || '',
    emergency_contact_relationship: m.emergency_contact_relationship || m.emergency_contact_rel || '',
    ...m,
    // re-apply after spread so normalised values win
    emergency_contact_rel:          m.emergency_contact_relationship || m.emergency_contact_rel || '',
    emergency_contact_relationship: m.emergency_contact_relationship || m.emergency_contact_rel || '',
    // settings JSONB from users table → preferences used by renderSettings
    preferences: (() => {
      const raw = m.settings || m.preferences || {};
      const def = {
        notifications: { email:true, push:true, sms:false, marketing:false, security_alerts:true, job_updates:true, team_updates:true },
        privacy:       { profile_visibility:'team', contact_visibility:'managers', activity_visibility:'private', search_visibility:true, data_sharing:false },
        security:      { two_factor_enabled:false, biometric_enabled:false, session_timeout:30, login_alerts:true, device_tracking:true },
      };
      return {
        notifications: { ...def.notifications, ...(raw.notifications || {}) },
        privacy:       { ...def.privacy,       ...(raw.privacy       || {}) },
        security:      { ...def.security,      ...(raw.security      || {}) },
      };
    })(),
  });

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setUserNotFound(false);

      // Try online with multiple endpoint strategies
      if (isOnline) {
        const cid = companyId || await getCompanyId();

        // Strategy 1: GET /api/company/:cid/team?user_id=:userId
        // (finds team member record by user_id)
        if (cid) {
          try {
            console.log('🌐 Loading profile online for user ID:', userId);
            const r = await apiGet(`/company/${cid}/team`);
            const members = Array.isArray(r.data) ? r.data : [];
            const member  = members.find(m =>
              String(m.user_id) === String(userId) || String(m.id) === String(userId)
            );
            if (member) {
              console.log('✅ Profile loaded from team members');
              const profile = normaliseTeamMember(member);
              setProfile(profile);
              setDraft(profile);
              await cacheProfile(profile);
              setOfflineMode(false);
              setCompanyTeam(members);
              // Load sub-data in background
              const uid = profile.user_id || profile.id || userId;
              if (uid) Promise.allSettled([loadSkills(uid), loadCertifications(uid), loadEducation(uid), loadExperience(uid)]);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Team fetch failed:', e.message);
          }
        }

        // Strategy 2: GET /api/users/:userId (users table)
        try {
          const r = await apiGet(`/data/users?id=${userId}&limit=1`); if (r.data && Array.isArray(r.data)) r.data = r.data[0];
          if (r.success && r.data) {
            console.log('✅ Profile loaded from users table');
            const profile = normaliseTeamMember(r.data);
            setProfile(profile);
            setDraft(profile);
            await cacheProfile(profile);
            setOfflineMode(false);
            // Load sub-data in background
            const uid = profile.user_id || profile.id || userId;
            if (uid) Promise.allSettled([loadSkills(uid), loadCertifications(uid), loadEducation(uid), loadExperience(uid)]);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Users table fetch failed:', e.message);
        }
      }

      // Fallback to cache
      console.log('📱 Loading profile from cache');
      const cached = await loadCachedProfile();
      if (cached) {
        setProfile(cached);
        setDraft(cached);
        setOfflineMode(true);
        // Try loading sub-data even in offline mode
        const uid = cached.user_id || cached.id || userId;
        if (uid) Promise.allSettled([loadSkills(uid), loadCertifications(uid), loadEducation(uid), loadExperience(uid)]);
      } else {
        // Create new user profile if nothing found
        await createNewUser();
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setUserNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSkills = async uid => {
    try {
      const response = await apiGet(`/users/${uid}/skills`);
      if (response.success) setSkills(response.data || []);
    } catch (error) {
      console.warn('Error loading skills:', error);
    }
  };

  const loadCertifications = async uid => {
    try {
      const response = await apiGet(`/users/${uid}/certifications`);
      if (response.success) setCerts(response.data || []);
    } catch (error) {
      console.warn('Error loading certifications:', error);
    }
  };

  const loadEducation = async uid => {
    try {
      const response = await apiGet(`/users/${uid}/education`);
      if (response.success) setEducation(response.data || []);
    } catch (error) {
      console.warn('Error loading education:', error);
    }
  };

  const loadExperience = async uid => {
    try {
      const response = await apiGet(`/users/${uid}/experience`);
      if (response.success) setExperience(response.data || []);
    } catch (error) {
      console.warn('Error loading experience:', error);
    }
  };

  // ── Cache helpers ────────────────────────────────────────────────────────────
  const cacheProfile = async data => {
    try {
      const key = data?.id ? `${CACHE_KEY}_${data.id}` : `${CACHE_KEY}_${userId || 'default'}`;
      await AsyncStorage.setItem(key, JSON.stringify(data));
      console.log('✅ Profile cached with key:', key);
    } catch (error) {
      console.warn('Cache error:', error);
    }
  };

  const loadCachedProfile = async (customId = null) => {
    try {
      const key = customId
        ? `${CACHE_KEY}_${customId}`
        : `${CACHE_KEY}_${userId || 'default'}`;
      const cached = await AsyncStorage.getItem(key);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  };

  // ── Company profile ─────────────────────────────────────────────────────────
  const fetchCompanyTeam = async () => {
    const cid = companyId || await getCompanyId();
    if (!cid) return;
    try {
      const r = await apiGet(`/company/${cid}/team`);
      const members = Array.isArray(r.data) ? r.data : [];
      setCompanyTeam(members);
      console.log(`[UserProfile] Team loaded: ${members.length} members for company ${cid}`);
    } catch (e) {
      console.warn('[UserProfile] fetchCompanyTeam:', e.message);
    }
  };

  const fetchCompanyProfile = async () => {
    const cid = companyId || await getCompanyId();
    if (!cid) return;
    if (!companyId) setCompanyId(cid);
    setCompanyLoading(true);
    try {
      // Load from storage cache first for instant render
      const cached = await storage.getItem('company_profile');
      if (cached) setCompanyProfile(JSON.parse(cached));
      if (isOnline) {
        const r = await apiGet(`/company/profile/${cid}`);
        if (r.success && r.data) {
          const cp = {
            id: String(r.data.id),
            name: r.data.company_name || '',
            email: r.data.email || '',
            phone: r.data.phone || '',
            website: r.data.website || '',
            industry: r.data.industry || '',
            companySize: r.data.employee_count ? String(r.data.employee_count) : '',
            logo: r.data.logo || null,
            companyCode: r.data.company_code || '',
            headquarters: r.data.address || '',
            description: r.data.description || '',
          };
          setCompanyProfile(cp);
          await storage.setItem('company_profile', JSON.stringify(cp));
        }
      }
    } catch { /* use cache */ }
    finally { setCompanyLoading(false); }
  };

  // Keeps team_members record consistent with user profile
  const syncToTeamMembers = async (p) => {
    const cid = companyId || await AsyncStorage.getItem('company_id');
    if (!cid || !p?.employee_id || !isOnline) return;
    try {
      const r = await apiGet(`/company/${cid}/team`);
      const tm = (r.data || [])[0];
      if (tm?.id) {
        await apiPut(`/company/${cid}/team/${tm.id}`, {
          company_id: Number(cid),
          name:          p.full_name     || tm.name,
          role:          p.job_title     || tm.role,
          department:    p.department    || tm.department,
          email:         p.email         || tm.email,
          phone:         p.phone         || tm.phone,
          status:        p.status        || tm.status || 'active',
          profile_image: p.profile_image || tm.profile_image,
        });
      }
    } catch { /* non-critical */ }
  };

  // ── Profile CRUD ─────────────────────────────────────────────────────────────
  const startEdit = () => {
    console.log('✏️ Edit mode started');
    console.log('Current profile data:', profile);
    if (!profile) {
      Alert.alert('Error', 'Profile not loaded');
      return;
    }
    setDraft({ ...profile });
    setEditing(true);
  };

  const cancelEdit = () => {
    console.log('❌ Edit cancelled');
    setDraft({ ...profile });
    setEditing(false);
  };

  /**
   * Save profile - removes auto-managed fields before sending to API
   * This prevents the "multiple assignments to same column" error
   */
  const saveProfile = async () => {
    console.log('💾 Save profile started');
    console.log('Draft data:', draft);

    if (!draft?.full_name?.trim()) {
      Alert.alert('Required', 'Full name is required');
      return;
    }
    if (!draft?.email?.trim()) {
      Alert.alert('Required', 'Email is required');
      return;
    }

    setSaving(true);

    try {
      // Prepare data for API - remove fields that are auto-managed by the database
      // This includes all timestamp fields, audit fields, and system-managed fields
      const { 
        id, 
        user_id,
        created_at, 
        updated_at, 
        created_by, 
        updated_by, 
        last_login_at,
        login_count,
        failed_login_attempts,
        locked_until,
        email_verified,
        email_verified_at,
        email_verification_token,
        two_factor_secret,
        two_factor_backup_codes,
        devices,
        active_sessions,
        metadata,
        password_hash,
        password_salt,
        password_updated_at,
        ...profileData 
      } = draft;
      
      console.log('📤 Sending to API (cleaned):', profileData);

      // Try online save first
      if (isOnline) {
        try {
          const cid = await getCompanyId();

          // Fix field name: screen uses emergency_contact_rel, DB uses emergency_contact_relationship
          const apiPayload = {
            ...profileData,
            emergency_contact_relationship: draft.emergency_contact_relationship || draft.emergency_contact_rel || '',
          };
          delete apiPayload.emergency_contact_rel;

          // Fields that live in the users table (not team_members)
          const usersPayload = {
            full_name:                      apiPayload.full_name,
            phone:                          apiPayload.phone,
            bio:                            apiPayload.bio,
            address:                        apiPayload.address,
            date_of_birth:                  apiPayload.date_of_birth,
            emergency_contact_name:         apiPayload.emergency_contact_name,
            emergency_contact_phone:        apiPayload.emergency_contact_phone,
            emergency_contact_email:        apiPayload.emergency_contact_email,
            emergency_contact_relationship: apiPayload.emergency_contact_relationship,
            emergency_contact_address:      apiPayload.emergency_contact_address,
            city:                           apiPayload.city,
            country:                        apiPayload.country,
            postal_code:                    apiPayload.postal_code,
            gender:                         apiPayload.gender,
            nationality:                    apiPayload.nationality,
            linkedin:                       apiPayload.linkedin,
            github:                         apiPayload.github,
            twitter:                        apiPayload.twitter,
            portfolio_url:                  apiPayload.portfolio_url,
            personal_website:               apiPayload.personal_website,
          };

          // Save to both tables in parallel
          const [teamRes, usersRes] = await Promise.allSettled([
            apiPut(`/company/${cid}/team/${userId}`, apiPayload),
            apiPut(`/users/${userId}`, usersPayload),
          ]);

          const response = teamRes.status === 'fulfilled' ? teamRes.value : null;
          console.log('📥 Team API Response:', response);
          console.log('📥 Users API Response:', usersRes.status === 'fulfilled' ? usersRes.value : usersRes.reason);

          if (response?.success) {
            const merged = { ...draft, ...response.data, emergency_contact_rel: apiPayload.emergency_contact_relationship };
            await cacheProfile(merged);
            setProfile(merged);
            setDraft(merged);
            setEditing(false);
            // Keep team_members record in sync
            syncToTeamMembers(merged);
            Alert.alert('Success', 'Profile updated successfully');
            return;
          }
        } catch (error) {
          console.error('❌ Online save failed:', error);
          
          // If it's the updated_at error, try again with an even cleaner payload
          if (error.error && error.error.includes('multiple assignments to same column "updated_at"')) {
            console.log('🔄 Retrying without any timestamp fields...');
            
            // Remove ALL possible timestamp-related fields
            const { 
              created_at, 
              updated_at, 
              last_login_at,
              password_updated_at,
              ...cleanData 
            } = draft;

            const cleanPayload = {
              ...cleanData,
              emergency_contact_relationship: draft.emergency_contact_relationship || draft.emergency_contact_rel || '',
            };
            delete cleanPayload.emergency_contact_rel;
            
            try {
              const retryResponse = await apiPut(`/company/${await getCompanyId()}/team/${userId}`, cleanPayload);
              if (retryResponse.success) {
                const merged = { ...draft, ...retryResponse.data, emergency_contact_rel: cleanPayload.emergency_contact_relationship };
                await cacheProfile(merged);
                setProfile(merged);
                setDraft(merged);
                setEditing(false);
                syncToTeamMembers(merged);
                Alert.alert('Success', 'Profile updated successfully');
                return;
              }
            } catch (retryError) {
              console.error('❌ Retry also failed:', retryError);
            }
          }
          
          console.warn('Online save failed, saving locally:', error);
        }
      } else {
        console.log('📱 Offline mode - saving locally');
      }

      // Offline save
      await cacheProfile(draft);
      setProfile(draft);
      setDraft(draft);
      setEditing(false);
      setOfflineMode(true);
      Alert.alert('Saved Offline', 'Profile saved locally. Will sync when online.');
    } catch (error) {
      console.error('❌ Save error:', error);
      Alert.alert('Error', 'Could not save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Profile image upload ─────────────────────────────────────────────────────
  const pickImage = async (fromCamera = false) => {
    const { status } = fromCamera
      ? await ImagePicker.requestCameraPermissionsAsync()
      : await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Please grant permission to access your photos.');
      return;
    }

    const result = fromCamera
      ? await ImagePicker.launchCameraAsync({ allowsEditing: true, aspect: [1, 1], quality: 0.6, base64: true })
      : await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: true,
          aspect: [1, 1],
          quality: 0.6,
          base64: true,
        });

    if (!result.canceled && result.assets?.[0]) {
      const asset = result.assets[0];
      setImgModal(false);
      // Use base64 data URI so the server can store it (file:// URIs are local-only)
      // Pass raw uri + base64 separately so upload can use multipart
      await saveProfileImage(asset.uri, asset.base64 || null);
    }
  };

  const saveProfileImage = async (uri, base64Data) => {
    setUploadingImg(true);
    try {
      // Optimistic UI update with local URI immediately
      const localUri = base64Data ? `data:image/jpeg;base64,${base64Data}` : uri;
      const optimistic = { ...profile, profile_image: localUri };
      setProfile(optimistic);
      setDraft(optimistic);
      await cacheProfile(optimistic);

      if (isOnline) {
        const companyId = await getCompanyId();
        if (!companyId || !userId) {
          console.warn('⚠️ No companyId or userId — image cached locally only');
          return;
        }

        // Try all upload strategies — function handles fallback chain internally
        const savedUri = await apiUploadImage(companyId, userId, uri, base64Data, profile?.email);

        // Update profile with server-returned URI (may be a CDN URL)
        if (savedUri && savedUri !== localUri) {
          const withServerUri = { ...profile, profile_image: savedUri };
          setProfile(withServerUri);
          setDraft(withServerUri);
          await cacheProfile(withServerUri);
        }

        // Also sync to AsyncStorage caches used by other screens
        const allKeys = ['userProfile', 'company_profile'];
        for (const key of allKeys) {
          try {
            const raw = await AsyncStorage.getItem(key);
            if (raw) {
              const parsed = JSON.parse(raw);
              parsed.profile_image = savedUri;
              parsed.avatar = savedUri;
              await AsyncStorage.setItem(key, JSON.stringify(parsed));
            }
          } catch {}
        }

        Alert.alert('✅ Photo Saved', 'Your profile photo has been updated.');
      } else {
        console.log('📱 Offline mode — image cached locally');
        Alert.alert('Saved Offline', 'Photo saved locally and will sync when you reconnect.');
      }
    } catch (error) {
      console.error('Image save error:', error);
      Alert.alert('Error', `Failed to save photo: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingImg(false);
    }
  };

  const removeImage = () => {
    Alert.alert('Remove Photo', 'Remove your profile photo?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          const updated = { ...profile, profile_image: null };
          setProfile(updated);
          setDraft(updated);
          setImgModal(false);
          await cacheProfile(updated);

          if (isOnline) {
            try {
              await apiPut(`/company/${await getCompanyId()}/team/${userId}`, { profile_image: null });
            } catch (error) {
              console.warn('Remove sync failed');
            }
          }
        },
      },
    ]);
  };

  // ── Skills CRUD ──────────────────────────────────────────────────────────────
  const openAddSkill = () => {
    setEditSkill(null);
    setSkillForm({ name: '', proficiency: 2 });
    setSkillModal(true);
  };

  const openEditSkill = s => {
    setEditSkill(s);
    setSkillForm({
      name: s.skill_name || s.name || '',
      proficiency: s.skill_level || s.proficiency || 2,
    });
    setSkillModal(true);
  };

  const saveSkill = async () => {
    if (!skillForm.name?.trim()) {
      Alert.alert('Required', 'Skill name is required');
      return;
    }
    const uid = userId || parseInt(await AsyncStorage.getItem('user_id')) || null;
    if (!uid) { Alert.alert('Error', 'User session not found. Please log in again.'); return; }
    try {
      const skillData = { user_id: uid, skill_name: skillForm.name.trim(), skill_level: skillForm.proficiency };
      if (editSkill?.id) {
        await apiPut(`/users/${uid}/skills/${editSkill.id}`, skillData);
      } else {
        await apiPost(`/users/${uid}/skills`, skillData);
      }
      setSkillModal(false);
      await loadSkills(uid);
      Alert.alert('Success', 'Skill saved successfully');
    } catch (error) {
      console.error('Error saving skill:', error?.message || error);
      Alert.alert('Error', `Could not save skill: ${error?.message || 'Unknown error'}`);
    }
  };

  const deleteSkill = s => {
    Alert.alert('Remove Skill', `Remove "${s.skill_name || s.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiDelete(`/users/${userId}/skills/${s.id}`);
            await loadSkills(userId);
          } catch (error) {
            Alert.alert('Error', 'Could not remove skill');
          }
        },
      },
    ]);
  };

  // ── Certifications CRUD ──────────────────────────────────────────────────────
  const openAddCert = () => {
    setEditCert(null);
    setCertForm({ name: '', issuer: '', issue_date: '', expiry_date: '' });
    setCertModal(true);
  };

  const openEditCert = c => {
    setEditCert(c);
    setCertForm({
      name: c.cert_name || c.name || '',
      issuer: c.issuing_organization || c.issuer || '',
      issue_date: c.issue_date || '',
      expiry_date: c.expiry_date || '',
    });
    setCertModal(true);
  };

  const saveCert = async () => {
    if (!certForm.name?.trim()) {
      Alert.alert('Required', 'Certification name is required');
      return;
    }
    const uid = userId || parseInt(await AsyncStorage.getItem('user_id')) || null;
    if (!uid) { Alert.alert('Error', 'User session not found. Please log in again.'); return; }
    try {
      const certData = {
        user_id: uid,
        cert_name: certForm.name.trim(),
        issuing_organization: (certForm.issuer || '').trim() || null,
        issue_date: certForm.issue_date || null,
        expiry_date: certForm.expiry_date || null,
      };
      if (editCert?.id) {
        await apiPut(`/users/${uid}/certifications/${editCert.id}`, certData);
      } else {
        await apiPost(`/users/${uid}/certifications`, certData);
      }
      setCertModal(false);
      await loadCertifications(uid);
      Alert.alert('Success', 'Certification saved successfully');
    } catch (error) {
      console.error('Error saving certification:', error?.message || error);
      Alert.alert('Error', `Could not save certification: ${error?.message || 'Unknown error'}`);
    }
  };

  const deleteCert = c => {
    Alert.alert('Remove Certification', `Remove "${c.cert_name || c.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiDelete(`/users/${userId}/certifications/${c.id}`);
            await loadCertifications(userId);
          } catch (error) {
            Alert.alert('Error', 'Could not remove certification');
          }
        },
      },
    ]);
  };

  // ── Education CRUD ───────────────────────────────────────────────────────────
  const openAddEdu = () => {
    setEditEdu(null);
    setEduForm({
      institution: '',
      degree: '',
      field: '',
      start_year: '',
      end_year: '',
      grade: '',
    });
    setEduModal(true);
  };

  const openEditEdu = e => {
    setEditEdu(e);
    setEduForm({
      institution: e.institution || '',
      degree: e.degree || '',
      field: e.field_of_study || e.field || '',
      start_year: String(e.start_year || e.start_date || ''),
      end_year: String(e.end_year || e.end_date || ''),
      grade: e.grade || '',
    });
    setEduModal(true);
  };

  const saveEdu = async () => {
    if (!eduForm.institution?.trim()) {
      Alert.alert('Required', 'Institution name is required');
      return;
    }

    const uid = userId || parseInt(await AsyncStorage.getItem('user_id')) || null;
    if (!uid) {
      Alert.alert('Error', 'User session not found. Please log in again.');
      return;
    }

    try {
      const eduData = {
        user_id: uid,
        institution:   (eduForm.institution || '').trim(),
        degree:        (eduForm.degree       || '').trim() || null,
        field_of_study:(eduForm.field        || '').trim() || null,
        start_date:    eduForm.start_year    ? String(eduForm.start_year).trim() : null,
        end_date:      eduForm.end_year      ? String(eduForm.end_year).trim()   : null,
        grade:         (eduForm.grade        || '').trim() || null,
      };

      if (editEdu?.id) {
        await apiPut(`/users/${uid}/education/${editEdu.id}`, eduData);
      } else {
        await apiPost(`/users/${uid}/education`, eduData);
      }

      setEduModal(false);
      await loadEducation(uid);
      Alert.alert('Success', 'Education saved successfully');
    } catch (error) {
      console.error('Error saving education:', error?.message || error);
      Alert.alert('Error', `Could not save education: ${error?.message || 'Unknown error'}`);
    }
  };

  const deleteEdu = e => {
    Alert.alert('Remove Education', `Remove "${e.institution}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiDelete(`/users/${userId}/education/${e.id}`);
            await loadEducation(userId);
          } catch (error) {
            Alert.alert('Error', 'Could not remove education');
          }
        },
      },
    ]);
  };

  // ── Work Experience CRUD ─────────────────────────────────────────────────────
  const openAddExp = () => {
    setEditExp(null);
    setExpForm({
      company: '',
      role: '',
      start_date: '',
      end_date: '',
      current: false,
      description: '',
    });
    setExpModal(true);
  };

  const openEditExp = e => {
    setEditExp(e);
    setExpForm({
      company: e.company_name || e.company || '',
      role: e.job_title || e.role || '',
      start_date: e.start_date || '',
      end_date: e.end_date || '',
      current: !!e.is_current || !!e.current,
      description: e.description || '',
    });
    setExpModal(true);
  };

  const saveExp = async () => {
    if (!expForm.company?.trim()) {
      Alert.alert('Required', 'Company name is required');
      return;
    }
    if (!expForm.role?.trim()) {
      Alert.alert('Required', 'Job title is required');
      return;
    }
    const uid = userId || parseInt(await AsyncStorage.getItem('user_id')) || null;
    if (!uid) { Alert.alert('Error', 'User session not found. Please log in again.'); return; }
    try {
      const expData = {
        user_id: uid,
        company_name: expForm.company.trim(),
        job_title:    expForm.role.trim(),
        start_date:   expForm.start_date  || null,
        end_date:     expForm.current ? null : (expForm.end_date || null),
        is_current:   expForm.current,
        description:  (expForm.description || '').trim() || null,
      };
      if (editExp?.id) {
        await apiPut(`/users/${uid}/experience/${editExp.id}`, expData);
      } else {
        await apiPost(`/users/${uid}/experience`, expData);
      }
      setExpModal(false);
      await loadExperience(uid);
      Alert.alert('Success', 'Experience saved successfully');
    } catch (error) {
      console.error('Error saving experience:', error?.message || error);
      Alert.alert('Error', `Could not save experience: ${error?.message || 'Unknown error'}`);
    }
  };

  const deleteExp = e => {
    Alert.alert('Remove Experience', `Remove "${e.job_title || e.role}" at "${e.company_name || e.company}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiDelete(`/users/${userId}/experience/${e.id}`);
            await loadExperience(userId);
          } catch (error) {
            Alert.alert('Error', 'Could not remove experience');
          }
        },
      },
    ]);
  };

  // ── Password change ──────────────────────────────────────────────────────────
  const changePassword = async () => {
    if (!pwForm.current.trim()) {
      Alert.alert('Required', 'Current password is required');
      return;
    }
    if (pwForm.next.length < 8) {
      Alert.alert('Too short', 'New password must be at least 8 characters');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      Alert.alert('Mismatch', 'Passwords do not match');
      return;
    }

    setChangingPw(true);
    try {
      await apiPost('/auth/change-password', {
        user_id: userId,
        current_password: pwForm.current,
        new_password: pwForm.next,
      });

      setPwModal(false);
      setPwForm({ current: '', next: '', confirm: '' });
      Alert.alert('Success', 'Password changed successfully');
    } catch (error) {
      Alert.alert('Error', error?.error || 'Could not change password');
    } finally {
      setChangingPw(false);
    }
  };

  // ── Save preferences ─────────────────────────────────────────────────────────
  const savePreference = async (field, value) => {
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    setDraft(updated);
    await cacheProfile(updated);

    if (isOnline) {
      try {
        // Save to both team record and users table
        await Promise.allSettled([
          apiPut(`/company/${await getCompanyId()}/team/${userId}`, { [field]: value }),
          apiPut(`/users/${userId}`, { [field]: value }),
        ]);
      } catch (error) {
        console.warn('Preference sync failed:', error?.message);
      }
    }
  };

  const saveNestedPref = async (parent, child, value) => {
    const updated = {
      ...profile,
      [parent]: {
        ...(profile?.[parent] || {}),
        [child]: value,
      },
    };
    setProfile(updated);
    setDraft(updated);
    await cacheProfile(updated);

    if (isOnline) {
      try {
        // Settings live in users.settings JSONB — not in team_members
        await apiPut(`/users/${userId}`, {
          settings: {
            notifications: updated.preferences?.notifications || {},
            privacy:       updated.preferences?.privacy       || {},
            security:      updated.preferences?.security      || {},
          },
        });
      } catch (error) {
        console.warn('Nested preference sync failed:', error?.message);
      }
    }
  };

  // ── Computed values ──────────────────────────────────────────────────────────
  const p = editing ? draft : profile;

  const completeness = (() => {
    if (!p) return 0;
    const checks = [
      p.full_name,
      p.email,
      p.phone,
      p.date_of_birth,
      p.job_title,
      p.department,
      p.manager,
      p.emergency_contact_name,
      p.emergency_contact_phone,
      p.profile_image,
      skills.length > 0,
      certs.length > 0,
      education.length > 0,
      experience.length > 0,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  })();

  // ── Modals ───────────────────────────────────────────────────────────────────






  // ── Helper functions ─────────────────────────────────────────────────────────
  const getProficiencyColor = level => {
    const colors = ['#D32F2F', '#F57C00', '#1976D2', '#388E3C'];
    return colors[level - 1] || '#1976D2';
  };

  const formatDate = date => {
    if (!date) return '—';
    try {
      return new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  };

  const isCertExpired = cert => {
    if (!cert.expiry_date) return false;
    return new Date(cert.expiry_date) < new Date();
  };

  const isCertExpiring = cert => {
    if (!cert.expiry_date) return false;
    const diff = (new Date(cert.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 90;
  };

  // ── Render functions ─────────────────────────────────────────────────────────
  // ── PDF Export ───────────────────────────────────────────────────────────────
  const generateProfilePDF = async () => {
    setExportingPdf(true);
    try {
      // Ensure sub-data is loaded before generating PDF
      const uid = userId || parseInt(await AsyncStorage.getItem('user_id')) || null;
      if (uid) {
        await Promise.allSettled([
          skills.length     === 0 ? loadSkills(uid)         : Promise.resolve(),
          certs.length      === 0 ? loadCertifications(uid) : Promise.resolve(),
          education.length  === 0 ? loadEducation(uid)      : Promise.resolve(),
          experience.length === 0 ? loadExperience(uid)     : Promise.resolve(),
        ]);
      }

      let photoSrc = '';
      if (p?.profile_image) {
        try {
          if (p.profile_image.startsWith('data:')) {
            // Already base64 — use directly
            photoSrc = p.profile_image;
          } else if (p.profile_image.startsWith('http')) {
            // Fetch via JS fetch() — avoids FileSystem.downloadAsync permission issues
            try {
              const resp = await Promise.race([
                fetch(p.profile_image),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000)),
              ]);
              if (resp.ok) {
                const blob = await resp.blob();
                photoSrc = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });
              }
            } catch {
              // Fallback: try FileSystem.downloadAsync
              try {
                const dl = await FileSystem.downloadAsync(
                  p.profile_image,
                  FileSystem.cacheDirectory + 'profile_photo_' + Date.now() + '.jpg'
                );
                const b64 = await FileSystem.readAsStringAsync(dl.uri, { encoding: FileSystem.EncodingType.Base64 });
                photoSrc = `data:image/jpeg;base64,${b64}`;
              } catch { photoSrc = ''; }
            }
          } else if (p.profile_image.startsWith('file://') || p.profile_image.startsWith('/')) {
            try {
              const b64 = await FileSystem.readAsStringAsync(p.profile_image, { encoding: FileSystem.EncodingType.Base64 });
              photoSrc = `data:image/jpeg;base64,${b64}`;
            } catch { photoSrc = ''; }
          }
        } catch { photoSrc = ''; }
      }

      const fd = (v) => v ? new Date(v).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
      const fv = (label, value) => value ? `<div class="field"><div class="fl">${label}</div><div class="fv">${value}</div></div>` : '';
      const profLabel = lvl => (['','Beginner','Intermediate','Advanced','Expert'][lvl] || 'Intermediate');
      const initials = (name) => (name||'?').split(' ').slice(0,2).map(w=>w[0]?.toUpperCase()||'').join('');

      const skillRows = (skills||[]).map(s => `
        <tr>
          <td>${s.skill_name||s.name||''}</td>
          <td><span class="badge b-blue">${profLabel(s.skill_level||s.proficiency||2)}</span></td>
          <td>${s.years_experience ? s.years_experience+' yrs' : '—'}</td>
          <td>${s.is_primary ? '★' : ''}</td>
        </tr>`).join('');

      const certRows = (certs||[]).map(c => {
        const exp = c.expiry_date && new Date(c.expiry_date) < new Date();
        return `
        <tr>
          <td>${c.cert_name||c.name||''}</td>
          <td>${c.issuing_organization||'—'}</td>
          <td>${fd(c.issue_date)}</td>
          <td>${c.expiry_date ? `<span class="badge ${exp?'b-red':'b-green'}">${fd(c.expiry_date)}${exp?' ✕':''}</span>` : '—'}</td>
          <td>${c.credential_id||'—'}</td>
        </tr>`;
      }).join('');

      const expItems = (experience||[]).map(e => `
        <div class="tl-item">
          <div class="tl-dot"></div>
          <div class="tl-body">
            <div class="tl-title">${e.job_title||e.role||''}</div>
            <div class="tl-sub">${e.company_name||e.company||''}${e.location?' · '+e.location:''}</div>
            <div class="tl-date">${fd(e.start_date)} — ${e.is_current?'Present':fd(e.end_date)}</div>
            ${e.description?`<div class="tl-desc">${e.description}</div>`:''}
          </div>
        </div>`).join('');

      const eduItems = (education||[]).map(e => `
        <div class="tl-item">
          <div class="tl-dot edu"></div>
          <div class="tl-body">
            <div class="tl-title">${e.institution||''}</div>
            <div class="tl-sub">${[e.degree,e.field_of_study].filter(Boolean).join(' in ')}</div>
            <div class="tl-date">${e.start_date||''} — ${e.end_date||'Present'}</div>
            ${e.grade?`<div class="tl-desc">Grade: ${e.grade}</div>`:''}
            ${e.description?`<div class="tl-desc">${e.description}</div>`:''}
          </div>
        </div>`).join('');

      const langArr = Array.isArray(p?.languages) ? p.languages
        : (p?.languages ? String(p.languages).split(',').map(s=>s.trim()) : []);
      const langTags = langArr.map(l => `<span class="tag">${typeof l==='object'?(l.language_name||l.name||''):l}</span>`).join(' ');

      const socials = [
        p?.linkedin        && `<span class="tag">🔗 LinkedIn: ${p.linkedin}</span>`,
        p?.github          && `<span class="tag">💻 GitHub: ${p.github}</span>`,
        p?.twitter         && `<span class="tag">🐦 Twitter: ${p.twitter}</span>`,
        p?.portfolio_url   && `<span class="tag">🌐 Portfolio: ${p.portfolio_url}</span>`,
        p?.personal_website&& `<span class="tag">🌍 Website: ${p.personal_website}</span>`,
      ].filter(Boolean).join(' ');

      const companyName = companyProfile?.company_name || companyProfile?.name || p?.company_name || '';

      const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,Helvetica,sans-serif;color:#1a2332;background:#fff;font-size:12px;}
.page{max-width:794px;margin:0 auto;padding:36px 40px;}
.header{background:linear-gradient(135deg,#0d3d7a,#1565C0);color:#fff;padding:28px 32px;border-radius:14px;display:flex;align-items:center;gap:24px;margin-bottom:22px;}
.avatar{width:110px;height:110px;border-radius:55px;border:4px solid rgba(255,255,255,0.5);object-fit:cover;flex-shrink:0;}
.avatar-ph{width:110px;height:110px;border-radius:55px;border:4px solid rgba(255,255,255,0.4);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:40px;font-weight:900;color:#fff;background:rgba(255,255,255,0.15);}
.hinfo{flex:1;}
.hname{font-size:26px;font-weight:900;letter-spacing:-0.5px;margin-bottom:4px;}
.hrole{font-size:13px;opacity:0.88;margin-bottom:10px;}
.hbadges{display:flex;flex-wrap:wrap;gap:7px;}
.hbadge{background:rgba(255,255,255,0.18);border:1px solid rgba(255,255,255,0.3);border-radius:20px;padding:3px 11px;font-size:10px;font-weight:700;}
.bio-strip{background:#F0F4FF;border-left:4px solid #0e4e96;border-radius:0 8px 8px 0;padding:11px 15px;margin-bottom:20px;font-size:12px;color:#37474F;line-height:1.6;font-style:italic;}
.section{margin-bottom:20px;page-break-inside:avoid;}
.sec-title{font-size:10px;font-weight:800;color:#0e4e96;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #BBDEFB;padding-bottom:5px;margin-bottom:12px;}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:7px;}
.grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:7px;}
.field{background:#F8FAFC;border:1px solid #E8EDF2;border-radius:7px;padding:8px 12px;}
.fl{font-size:9px;color:#90A4AE;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:2px;}
.fv{font-size:12px;color:#263238;font-weight:600;word-break:break-word;}
table{width:100%;border-collapse:collapse;}
th{background:#EEF2FF;color:#4A5568;font-size:9px;text-transform:uppercase;letter-spacing:0.8px;padding:7px 10px;text-align:left;font-weight:700;}
td{padding:7px 10px;border-bottom:1px solid #F0F4F8;font-size:11px;color:#37474F;vertical-align:top;}
tr:last-child td{border-bottom:none;}
.badge{display:inline-block;border-radius:5px;padding:2px 7px;font-size:10px;font-weight:700;}
.b-blue{background:#DBEAFE;color:#1D4ED8;}
.b-green{background:#D1FAE5;color:#065F46;}
.b-red{background:#FEE2E2;color:#991B1B;}
.tl{border-left:3px solid #BBDEFB;margin-left:8px;padding-left:20px;}
.tl-item{position:relative;margin-bottom:16px;}
.tl-dot{width:12px;height:12px;border-radius:50%;background:#0e4e96;position:absolute;left:-26px;top:3px;border:2px solid #fff;box-shadow:0 0 0 2px #BBDEFB;}
.tl-dot.edu{background:#7B1FA2;box-shadow:0 0 0 2px #E9D5FF;}
.tl-title{font-size:13px;font-weight:800;color:#1a2332;}
.tl-sub{font-size:12px;color:#0e4e96;font-weight:600;margin-top:2px;}
.tl-date{font-size:10px;color:#94A3B8;margin-top:2px;font-weight:600;}
.tl-desc{font-size:11px;color:#546E7A;margin-top:5px;line-height:1.5;}
.tag{display:inline-block;background:#EEF2FF;color:#3730A3;border-radius:12px;padding:3px 10px;font-size:11px;font-weight:600;margin:2px;}
.emergency-box{background:#FFF5F5;border:1px solid #FED7D7;border-radius:10px;padding:13px 15px;}
.emergency-title{color:#C53030;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:10px;}
.footer{margin-top:32px;border-top:2px solid #EEF2FF;padding-top:10px;display:flex;justify-content:space-between;color:#94A3B8;font-size:10px;}
</style></head><body><div class="page">

<div class="header">
  ${photoSrc?`<img class="avatar" src="${photoSrc}"/>`:`<div class="avatar-ph">${initials(p?.full_name)}</div>`}
  <div class="hinfo">
    <div class="hname">${p?.full_name||'—'}</div>
    <div class="hrole">${[p?.job_title||p?.position,p?.department].filter(Boolean).join(' · ')||'Employee'}</div>
    <div class="hbadges">
      ${companyName?`<span class="hbadge">🏢 ${companyName}</span>`:''}
      ${p?.employee_id?`<span class="hbadge">🪪 ${p.employee_id}</span>`:''}
      ${p?.employment_type?`<span class="hbadge">${p.employment_type}</span>`:''}
      ${p?.status?`<span class="hbadge">${p.status}</span>`:''}
      ${p?.nationality?`<span class="hbadge">🌍 ${p.nationality}</span>`:''}
    </div>
  </div>
</div>

${(p?.bio||p?.about)?`<div class="bio-strip">${p.bio||p.about}</div>`:''}

<div class="section">
  <div class="sec-title">Personal Details</div>
  <div class="grid2">
    ${fv('Email',p?.email)}
    ${fv('Phone',p?.phone)}
    ${fv('Date of Birth',fd(p?.date_of_birth))}
    ${fv('Gender',p?.gender)}
    ${fv('Nationality',p?.nationality)}
    ${fv('NI Number',p?.ni_number)}
    ${fv('Address',p?.address)}
    ${fv('City',p?.city)}
    ${fv('Postal Code',p?.postal_code)}
    ${fv('Country',p?.country)}
    ${fv('Location',p?.location)}
  </div>
</div>

<div class="section">
  <div class="sec-title">Work Details</div>
  <div class="grid2">
    ${fv('Work Email',p?.work_email)}
    ${fv('Work Phone',p?.work_phone)}
    ${fv('Job Title',p?.job_title||p?.position)}
    ${fv('Department',p?.department)}
    ${fv('Manager',p?.manager||p?.manager_name)}
    ${fv('Employment Type',p?.employment_type)}
    ${fv('Start Date',fd(p?.start_date))}
    ${fv('Years Experience',p?.experience_years?p.experience_years+' yrs':null)}
  </div>
</div>

${(p?.emergency_contact_name||p?.emergency_contact_phone)?`
<div class="section">
  <div class="sec-title">Emergency Contact</div>
  <div class="emergency-box">
    <div class="emergency-title">⚠ Emergency Contact Information</div>
    <div class="grid2">
      ${fv('Name',p?.emergency_contact_name)}
      ${fv('Relationship',p?.emergency_contact_relationship||p?.emergency_contact_rel)}
      ${fv('Phone',p?.emergency_contact_phone)}
      ${fv('Email',p?.emergency_contact_email)}
      ${fv('Address',p?.emergency_contact_address)}
    </div>
  </div>
</div>`:''}

${skillRows?`
<div class="section">
  <div class="sec-title">Skills</div>
  <table><tr><th>Skill</th><th>Level</th><th>Experience</th><th>Primary</th></tr>${skillRows}</table>
</div>`:''}

${certRows?`
<div class="section">
  <div class="sec-title">Certifications</div>
  <table><tr><th>Certification</th><th>Issued By</th><th>Issue Date</th><th>Expiry</th><th>Credential ID</th></tr>${certRows}</table>
</div>`:''}

${expItems?`
<div class="section">
  <div class="sec-title">Work Experience</div>
  <div class="tl">${expItems}</div>
</div>`:''}

${eduItems?`
<div class="section">
  <div class="sec-title">Education</div>
  <div class="tl">${eduItems}</div>
</div>`:''}

${langTags?`
<div class="section">
  <div class="sec-title">Languages</div>
  <div>${langTags}</div>
</div>`:''}

${socials?`
<div class="section">
  <div class="sec-title">Online Presence</div>
  <div>${socials}</div>
</div>`:''}

<div class="footer">
  <span>Generated ${new Date().toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})} · JobEng Platform · CONFIDENTIAL</span>
  <span>${p?.employee_id||''}</span>
</div>
</div></body></html>`;

      const { uri } = await Print.printToFileAsync({ html, base64: false });
      // Try to share directly from the generated uri first (most reliable)
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`${p?.full_name||'Profile'} — Profile PDF`, UTI:'com.adobe.pdf' });
      } else {
        // Fallback: move then alert
        try {
          const dest = FileSystem.cacheDirectory + `${(p?.full_name||'Profile').replace(/\s+/g,'_')}_Profile.pdf`;
          await FileSystem.moveAsync({ from: uri, to: dest });
          Alert.alert('PDF Saved', `Saved to: ${dest}`);
        } catch {
          Alert.alert('PDF Ready', 'PDF generated at: ' + uri);
        }
      }
    } catch (e) {
      console.error('PDF export error:', e);
      Alert.alert('Export Failed', 'Could not generate PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };


  const renderOverview = () => (
    <View>
      {/* Company banner — taps to full company profile */}
      {companyProfile && (
        <TouchableOpacity
          style={styles.companyBanner}
          onPress={() => navigation.navigate('CompanyProfileScreen', { companyProfile })}
          activeOpacity={0.85}>
          {companyProfile.logo
            ? <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }}
                style={{ width: 28, height: 28, borderRadius: 14, marginRight: 8 }} />
            : <View style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#0e4e96',
                alignItems: 'center', justifyContent: 'center', marginRight: 8 }}>
                <MaterialCommunityIcons name="domain" size={14} color="#fff" />
              </View>}
          <Text style={{ flex: 1, fontSize: 13, color: '#0e4e96', fontWeight: '700' }}
            numberOfLines={1}>{companyProfile.name}</Text>
          <MaterialCommunityIcons name="chevron-right" size={16} color="#90A4AE" />
        </TouchableOpacity>
      )}

      <View style={styles.heroCard}>
        <View style={{ alignItems: 'center' }}>
          {uploadingImg ? (
            <View
              style={{
                width: 88,
                height: 88,
                borderRadius: 44,
                backgroundColor: '#E0E0E0',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ActivityIndicator color="#0e4e96" />
            </View>
          ) : (
            <Avatar
              uri={p?.profile_image}
              name={p?.full_name}
              size={88}
              onPress={() => setImgModal(true)}
              editing
            />
          )}
          <Text style={styles.heroName}>{p?.full_name || '—'}</Text>
          <Text style={styles.heroRole}>{p?.job_title || '—'}</Text>

          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            {p?.department && (
              <View style={styles.heroBadge}>
                <Text style={styles.heroBadgeText}>{p.department}</Text>
              </View>
            )}
            {p?.access_level && (
              <View style={[styles.heroBadge, { backgroundColor: '#E8F5E9', borderColor: '#A5D6A7' }]}>
                <Text style={[styles.heroBadgeText, { color: '#2E7D32' }]}>{p.access_level}</Text>
              </View>
            )}
          </View>

          <Completeness pct={completeness} />

          {offlineMode && (
            <View
              style={{
                marginTop: 10,
                backgroundColor: '#FFF3E0',
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 16,
              }}>
              <Text style={{ color: '#F57C00', fontSize: 12, fontWeight: '600' }}>📱 Offline Mode</Text>
            </View>
          )}
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 16, marginBottom: 10 }}>
        {[
          { label: 'Skills', value: skills.length, icon: 'star-outline', color: '#1976D2' },
          { label: 'Certs', value: certs.length, icon: 'certificate-outline', color: '#388E3C' },
          { label: 'Jobs', value: experience.length, icon: 'briefcase-outline', color: '#F57C00' },
          { label: 'Degrees', value: education.length, icon: 'school-outline', color: '#7B1FA2' },
        ].map(s => (
          <View key={s.label} style={[styles.statBox, { flex: 1 }]}>
            <MaterialCommunityIcons name={s.icon} size={20} color={s.color} />
            <Text style={[styles.statNum, { color: s.color }]}>{s.value}</Text>
            <Text style={styles.statLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      <SectionCard title="Key Information">
        <InfoRow icon="account-outline" label="Full Name" value={p?.full_name} />
        <InfoRow
          icon="email-outline"
          label="Email"
          value={p?.email}
          action={() => Linking.openURL(`mailto:${p?.email}`)}
          actionLabel="Mail"
        />
        <InfoRow
          icon="phone-outline"
          label="Phone"
          value={p?.phone}
          action={() => Linking.openURL(`tel:${p?.phone}`)}
          actionLabel="Call"
        />
        <InfoRow icon="office-building" label="Company" value={p?.company_name} />
        <InfoRow icon="badge-account" label="Employee ID" value={p?.employee_id} />
        <InfoRow icon="calendar-outline" label="Start Date" value={formatDate(p?.start_date)} />
        <InfoRow icon="map-marker-outline" label="Location" value={p?.office_location} />
      </SectionCard>

      <SectionCard title="Quick Actions">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {[
            {
              label: 'Share Profile',
              icon: 'share-variant',
              color: '#1976D2',
              action: () =>
                Share.share({
                  message: `${p?.full_name} · ${p?.job_title} · ${p?.email}`,
                  title: 'Profile',
                }),
            },
            {
              label: 'Change Photo',
              icon: 'camera',
              color: '#F57C00',
              action: () => setImgModal(true),
            },
            {
              label: 'Export PDF',
              icon: 'file-pdf-box',
              color: '#388E3C',
              action: generateProfilePDF,
              loading: exportingPdf,
            },
            {
              label: 'Change Password',
              icon: 'lock-reset',
              color: '#D32F2F',
              action: () => setPwModal(true),
            },
            {
              label: 'My Dashboard',
              icon: 'view-dashboard',
              color: '#7B1FA2',
              action: () => {
                const emp = {
                  id: p?.id || p?.user_id || userId,
                  serverId: p?.id || p?.user_id || userId,
                  employeeId: p?.employee_id || p?.id || userId,
                  name: p?.full_name || p?.name || '',
                  email: p?.email || '',
                  avatar: p?.profile_image || p?.avatar || null,
                  department: p?.department || '',
                  position: p?.job_title || p?.position || '',
                  jobTitle: p?.job_title || p?.position || '',
                  phone: p?.phone || '',
                  status: p?.status || 'active',
                  company_id: p?.company_id || companyId || null,
                };
                navigation.navigate('EmployeeDashboard', { employee: emp, companyId });
              },
            },
          ].map(a => (
            <TouchableOpacity
              key={a.label}
              onPress={a.action}
              style={{
                width: (width - 52) / 2,
                backgroundColor: a.color + '15',
                borderRadius: 12,
                padding: 14,
                alignItems: 'center',
                gap: 6,
                borderWidth: 1,
                borderColor: a.color + '30',
              }}>
              {a.loading
                ? <ActivityIndicator size={22} color={a.color} />
                : <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />}
              <Text style={{ fontSize: 12, color: a.color, fontWeight: '700', textAlign: 'center' }}>
                {a.loading ? 'Exporting…' : a.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </SectionCard>


    </View>
  );

  const renderPersonal = () => (
    <View>
      <SectionCard title="Personal Information">
        <FormField
          label="Full Name"
          value={p?.full_name}
          onChange={v => setDraft(d => ({ ...d, full_name: v }))}
          editing={editing}
          required
        />
        <FormField
          label="Email"
          value={p?.email}
          onChange={v => setDraft(d => ({ ...d, email: v }))}
          editing={editing}
          required
        />
        <FormField
          label="Phone"
          value={p?.phone}
          onChange={v => setDraft(d => ({ ...d, phone: v }))}
          editing={editing}
        />
        <FormField
          label="Date of Birth"
          value={p?.date_of_birth}
          onChange={v => setDraft(d => ({ ...d, date_of_birth: v }))}
          editing={editing}
          placeholder="YYYY-MM-DD"
        />
        <FormField
          label="Address"
          value={p?.address}
          onChange={v => setDraft(d => ({ ...d, address: v }))}
          editing={editing}
          multiline
        />
      </SectionCard>

      <SectionCard title="Emergency Contact">
        <FormField
          label="Contact Name"
          value={p?.emergency_contact_name}
          onChange={v => setDraft(d => ({ ...d, emergency_contact_name: v }))}
          editing={editing}
        />
        <FormField
          label="Relationship"
          value={p?.emergency_contact_rel}
          onChange={v => setDraft(d => ({ ...d, emergency_contact_rel: v }))}
          editing={editing}
          placeholder="e.g. Spouse, Parent"
        />
        <FormField
          label="Contact Phone"
          value={p?.emergency_contact_phone}
          onChange={v => setDraft(d => ({ ...d, emergency_contact_phone: v }))}
          editing={editing}
        />
        <FormField
          label="Contact Email"
          value={p?.emergency_contact_email}
          onChange={v => setDraft(d => ({ ...d, emergency_contact_email: v }))}
          editing={editing}
        />
      </SectionCard>
    </View>
  );

  const renderProfessional = () => (
    <View>
      <SectionCard title="Employment Details">
        <FormField
          label="Job Title"
          value={p?.job_title}
          onChange={v => setDraft(d => ({ ...d, job_title: v }))}
          editing={editing}
        />
        <FormField
          label="Department"
          value={p?.department}
          onChange={v => setDraft(d => ({ ...d, department: v }))}
          editing={editing}
        />
        <FormField
          label="Employee ID"
          value={p?.employee_id}
          onChange={v => setDraft(d => ({ ...d, employee_id: v }))}
          editing={editing}
        />
        <FormField
          label="Manager"
          value={p?.manager}
          onChange={v => setDraft(d => ({ ...d, manager: v }))}
          editing={editing}
        />
        <FormField
          label="Start Date"
          value={p?.start_date}
          onChange={v => setDraft(d => ({ ...d, start_date: v }))}
          editing={editing}
          placeholder="YYYY-MM-DD"
        />
      </SectionCard>

      <SectionCard title="Skills" onAdd={openAddSkill} addLabel="Add Skill">
        {skills.length === 0 ? (
          <Text style={styles.emptyText}>No skills added yet</Text>
        ) : (
          skills.map((s, i) => {
            const level = s.skill_level || s.proficiency || 2;
            const name = s.skill_name || s.name || '';
            return (
              <View key={s.id || i} style={styles.skillRow}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#263238' }}>{name}</Text>
                  <Text
                    style={{
                      fontSize: 11,
                      color: getProficiencyColor(level),
                      fontWeight: '600',
                      marginTop: 2,
                    }}>
                    {['Beginner', 'Intermediate', 'Advanced', 'Expert'][level - 1] || 'Intermediate'}
                  </Text>
                  <View
                    style={{
                      height: 4,
                      backgroundColor: '#ECEFF1',
                      borderRadius: 2,
                      marginTop: 5,
                      overflow: 'hidden',
                    }}>
                    <View
                      style={{
                        width: `${(level / 4) * 100}%`,
                        height: '100%',
                        backgroundColor: getProficiencyColor(level),
                        borderRadius: 2,
                      }}
                    />
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <TouchableOpacity onPress={() => openEditSkill(s)} style={styles.iconButton}>
                    <MaterialCommunityIcons name="pencil-outline" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => deleteSkill(s)}
                    style={[styles.iconButton, { backgroundColor: '#FFEBEE' }]}>
                    <MaterialCommunityIcons name="delete-outline" size={16} color="#D32F2F" />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </SectionCard>

      <SectionCard title="Certifications" onAdd={openAddCert} addLabel="Add Cert">
        {certs.length === 0 ? (
          <Text style={styles.emptyText}>No certifications added yet</Text>
        ) : (
          certs.map((c, i) => {
            const expired = isCertExpired(c);
            const expiring = isCertExpiring(c);
            const name = c.cert_name || c.name || '';
            return (
              <View
                key={c.id || i}
                style={[
                  styles.certRow,
                  expired && { borderLeftColor: '#D32F2F' },
                  expiring && !expired && { borderLeftColor: '#F57C00' },
                ]}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: 14, fontWeight: '700', color: '#263238' }}>{name}</Text>
                    {expired && (
                      <View style={styles.expiredBadge}>
                        <Text style={{ fontSize: 10, color: '#D32F2F', fontWeight: '700' }}>EXPIRED</Text>
                      </View>
                    )}
                    {expiring && !expired && (
                      <View style={[styles.expiredBadge, { backgroundColor: '#FFF3E0', borderColor: '#FFCC80' }]}>
                        <Text style={{ fontSize: 10, color: '#F57C00', fontWeight: '700' }}>EXPIRING</Text>
                      </View>
                    )}
                  </View>
                  {c.issuing_organization && (
                    <Text style={{ fontSize: 12, color: '#78909C', marginTop: 2 }}>
                      Issued by {c.issuing_organization}
                    </Text>
                  )}
                  {c.issue_date && (
                    <Text style={{ fontSize: 12, color: '#90A4AE' }}>Issued: {formatDate(c.issue_date)}</Text>
                  )}
                  {c.expiry_date && (
                    <Text
                      style={{
                        fontSize: 12,
                        color: expired ? '#D32F2F' : expiring ? '#F57C00' : '#90A4AE',
                      }}>
                      Expires: {formatDate(c.expiry_date)}
                    </Text>
                  )}
                </View>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <TouchableOpacity onPress={() => openEditCert(c)} style={styles.iconButton}>
                    <MaterialCommunityIcons name="pencil-outline" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => deleteCert(c)}
                    style={[styles.iconButton, { backgroundColor: '#FFEBEE' }]}>
                    <MaterialCommunityIcons name="delete-outline" size={16} color="#D32F2F" />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </SectionCard>
    </View>
  );

  const renderExperience = () => (
    <View>
      <SectionCard title="Work Experience" onAdd={openAddExp} addLabel="Add">
        {experience.length === 0 ? (
          <Text style={styles.emptyText}>No work experience added yet</Text>
        ) : (
          experience.map((e, i) => (
            <View key={e.id || i} style={styles.timelineItem}>
              <View style={styles.timelineDot} />
              {i < experience.length - 1 && <View style={styles.timelineLine} />}
              <View style={{ flex: 1, paddingBottom: 16 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: '#263238' }}>
                      {e.job_title || e.role}
                    </Text>
                    <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600', marginTop: 2 }}>
                      {e.company_name || e.company}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <MaterialCommunityIcons name="calendar-range" size={12} color="#90A4AE" />
                      <Text style={{ fontSize: 12, color: '#90A4AE' }}>
                        {formatDate(e.start_date)} — {e.is_current ? 'Present' : formatDate(e.end_date)}
                      </Text>
                      {e.is_current && (
                        <View style={{ backgroundColor: '#E3F2FD', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 }}>
                          <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '700' }}>CURRENT</Text>
                        </View>
                      )}
                    </View>
                    {e.description ? (
                      <Text style={{ fontSize: 13, color: '#546E7A', marginTop: 6, lineHeight: 18 }}>
                        {e.description}
                      </Text>
                    ) : null}
                  </View>
                  <View style={{ flexDirection: 'row', gap: 6, marginLeft: 8 }}>
                    <TouchableOpacity onPress={() => openEditExp(e)} style={styles.iconButton}>
                      <MaterialCommunityIcons name="pencil-outline" size={15} color="#0e4e96" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => deleteExp(e)}
                      style={[styles.iconButton, { backgroundColor: '#FFEBEE' }]}>
                      <MaterialCommunityIcons name="delete-outline" size={15} color="#D32F2F" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
          ))
        )}
      </SectionCard>

      <SectionCard title="Education" onAdd={openAddEdu} addLabel="Add">
        {education.length === 0 ? (
          <Text style={styles.emptyText}>No education records added yet</Text>
        ) : (
          education.map((e, i) => (
            <View key={e.id || i} style={[styles.timelineItem, { borderLeftColor: '#7B1FA2' }]}>
              <View style={[styles.timelineDot, { backgroundColor: '#7B1FA2' }]} />
              {i < education.length - 1 && <View style={[styles.timelineLine, { backgroundColor: '#CE93D8' }]} />}
              <View style={{ flex: 1, paddingBottom: 16 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: '#263238' }}>{e.institution}</Text>
                    {e.degree && (
                      <Text style={{ fontSize: 13, color: '#7B1FA2', fontWeight: '600', marginTop: 2 }}>
                        {e.degree}
                        {e.field_of_study ? ` · ${e.field_of_study}` : ''}
                      </Text>
                    )}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
                      <MaterialCommunityIcons name="calendar-range" size={12} color="#90A4AE" />
                      <Text style={{ fontSize: 12, color: '#90A4AE' }}>
                        {e.start_date || '?'} — {e.end_date || 'Present'}
                      </Text>
                    </View>
                    {e.grade ? (
                      <Text style={{ fontSize: 12, color: '#78909C', marginTop: 3 }}>Grade: {e.grade}</Text>
                    ) : null}
                  </View>
                  <View style={{ flexDirection: 'row', gap: 6, marginLeft: 8 }}>
                    <TouchableOpacity onPress={() => openEditEdu(e)} style={styles.iconButton}>
                      <MaterialCommunityIcons name="pencil-outline" size={15} color="#0e4e96" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => deleteEdu(e)}
                      style={[styles.iconButton, { backgroundColor: '#FFEBEE' }]}>
                      <MaterialCommunityIcons name="delete-outline" size={15} color="#D32F2F" />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
          ))
        )}
      </SectionCard>
    </View>
  );

  const renderCompanyTab = () => {
    const cp = companyProfile;
    return (
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 20 }}>
        {companyLoading && !cp
          ? <ActivityIndicator color="#0e4e96" style={{ paddingTop: 40 }} />
          : cp ? (
            <>
              {/* Company hero */}
              <SectionCard title="">
                <View style={{ alignItems: 'center', paddingBottom: 10 }}>
                  {cp.logo
                    ? <Image source={{ uri: (cp.logo && cp.logo.startsWith('http')) ? cp.logo : undefined }}
                        style={{ width: 80, height: 80, borderRadius: 40, borderWidth: 2, borderColor: '#E3F2FD', marginBottom: 12 }} />
                    : <LinearGradient colors={['#0e4e96','#1565C0']}
                        style={{ width: 80, height: 80, borderRadius: 40, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                        <MaterialCommunityIcons name="domain" size={36} color="#fff" />
                      </LinearGradient>}
                  <Text style={{ fontSize: 18, fontWeight: '900', color: '#263238', textAlign: 'center' }}>
                    {cp.name}
                  </Text>
                  {cp.industry
                    ? <Text style={{ fontSize: 13, color: '#78909C', marginTop: 3 }}>{cp.industry}</Text>
                    : null}
                  {cp.companyCode
                    ? <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 8,
                          backgroundColor: '#F3E5F5', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 4 }}>
                        <MaterialCommunityIcons name="key" size={12} color="#7B1FA2" />
                        <Text style={{ fontSize: 12, color: '#7B1FA2', fontWeight: '700' }}>{cp.companyCode}</Text>
                      </View>
                    : null}
                </View>
                <TouchableOpacity
                  style={styles.saveButton}
                  onPress={() => navigation.navigate('CompanyProfileScreen', { companyProfile: cp })}>
                  <MaterialCommunityIcons name="domain" size={18} color="#fff" />
                  <Text style={styles.saveButtonText}>View Full Company Profile</Text>
                </TouchableOpacity>
              </SectionCard>

              {/* My role */}
              <SectionCard title="My Role">
                <InfoRow icon="briefcase-outline"  label="Job Title"    value={p?.job_title} />
                <InfoRow icon="account-group"      label="Department"   value={p?.department} />
                <InfoRow icon="account-tie" label="Manager"      value={p?.manager} />
                <InfoRow icon="map-marker-outline" label="Office"       value={p?.office_location} />
                <InfoRow icon="calendar-check"     label="Started"      value={p?.start_date ? formatDate(p.start_date) : null} />
                <InfoRow icon="shield-account"     label="Access Level" value={p?.access_level} />
                <InfoRow icon="badge-account-horizontal"       label="Employee ID"  value={p?.employee_id} />
                <TouchableOpacity
                  onPress={startEdit}
                  style={{ marginTop: 10, borderWidth: 1.5, borderColor: '#0e4e96', borderRadius: 10,
                    paddingVertical: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <MaterialCommunityIcons name="pencil-outline" size={16} color="#0e4e96" />
                  <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '700' }}>Edit My Details</Text>
                </TouchableOpacity>
              </SectionCard>

              {/* Company contact snapshot */}
              <SectionCard title="Company Info">
                <InfoRow icon="email-outline"      label="Email"        value={cp.email} />
                <InfoRow icon="phone-outline"      label="Phone"        value={cp.phone} />
                <InfoRow icon="web"                label="Website"      value={cp.website} />
                <InfoRow icon="office-building"    label="HQ"           value={cp.headquarters} />
                {cp.companySize
                  ? <InfoRow icon="account-multiple" label="Size" value={`${cp.companySize} employees`} />
                  : null}
                {cp.description
                  ? <View style={{ backgroundColor: '#F5F7FA', borderRadius: 10, padding: 12, marginTop: 8 }}>
                      <Text style={{ fontSize: 13, color: '#546E7A', lineHeight: 20 }}>{cp.description}</Text>
                    </View>
                  : null}
              </SectionCard>

              {/* Quick actions */}
              <SectionCard title="Quick Actions">
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                  {[
                    { label: 'Team Management',  icon: 'account-group',  color: '#1976D2',
                      action: () => navigation.navigate('TeamManagementScreen') },
                    { label: 'Team Analytics',   icon: 'chart-bar',      color: '#388E3C',
                      action: () => navigation.navigate('TeamAnalyticsScreen') },
                    { label: 'Edit Company',     icon: 'domain',         color: '#F57C00',
                      action: () => navigation.navigate('CompanyProfileScreen', { companyProfile: cp, openEdit: true }) },
                    { label: 'Invite Member',    icon: 'account-plus',   color: '#7B1FA2',
                      action: () => navigation.navigate('CompanyProfileScreen', { companyProfile: cp, openInvite: true }) },
                    { label: 'Employee Dashboard', icon: 'view-dashboard', color: '#0e4e96',
                      action: () => {
                        const emp = {
                          id: p?.id || p?.user_id || userId,
                          serverId: p?.id || p?.user_id || userId,
                          employeeId: p?.employee_id || p?.id || userId,
                          name: p?.full_name || p?.name || '',
                          email: p?.email || '',
                          avatar: p?.profile_image || p?.avatar || null,
                          department: p?.department || '',
                          position: p?.job_title || p?.position || '',
                          jobTitle: p?.job_title || p?.position || '',
                          phone: p?.phone || '',
                          status: p?.status || 'active',
                          company_id: p?.company_id || companyId || null,
                        };
                        navigation.navigate('EmployeeDashboard', { employee: emp, companyId });
                      } },
                  ].map(a => (
                    <TouchableOpacity key={a.label} onPress={a.action}
                      style={{ width: (width - 52) / 2, backgroundColor: a.color + '15',
                        borderRadius: 12, padding: 14, alignItems: 'center', gap: 6,
                        borderWidth: 1, borderColor: a.color + '30' }}>
                      <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />
                      <Text style={{ fontSize: 12, color: a.color, fontWeight: '700', textAlign: 'center' }}>{a.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </SectionCard>
            </>
          ) : (
            <SectionCard title="">
              <View style={{ alignItems: 'center', paddingVertical: 24 }}>
                <MaterialCommunityIcons name="domain" size={52} color="#CFD8DC" />
                <Text style={{ fontSize: 15, color: '#90A4AE', fontWeight: '600', marginTop: 12 }}>
                  No company linked
                </Text>
                <Text style={{ fontSize: 13, color: '#B0BEC5', textAlign: 'center', marginTop: 6, lineHeight: 18 }}>
                  Ask your admin to share the company code, or set up a new company.
                </Text>
                <TouchableOpacity
                  style={[styles.saveButton, { marginTop: 16, paddingHorizontal: 24 }]}
                  onPress={() => navigation.navigate('CompanyProfileScreen')}>
                  <MaterialCommunityIcons name="plus-circle-outline" size={18} color="#fff" />
                  <Text style={styles.saveButtonText}>Set Up Company</Text>
                </TouchableOpacity>
              </View>
            </SectionCard>
          )}
      </ScrollView>
    );
  };

  const renderSettings = () => (
    <View>
      <SectionCard title="Security">
        <InfoRow
          icon="lock-reset"
          label="Change Password"
          action={() => setPwModal(true)}
          actionLabel="Change"
          color="#D32F2F"
        />
        <ToggleSetting
          label="Two-Factor Authentication"
          sub="Require a code when signing in"
          value={p?.preferences?.security?.two_factor_enabled}
          onChange={v =>
            saveNestedPref('preferences', 'security', {
              ...p?.preferences?.security,
              two_factor_enabled: v,
            })
          }
        />
        <ToggleSetting
          label="Login Alerts"
          sub="Email me when a new device signs in"
          value={p?.preferences?.security?.login_alerts}
          onChange={v =>
            saveNestedPref('preferences', 'security', {
              ...p?.preferences?.security,
              login_alerts: v,
            })
          }
        />
        <ToggleSetting
          label="Biometric Login"
          sub="Use fingerprint or Face ID"
          value={p?.preferences?.security?.biometric_enabled}
          onChange={v =>
            saveNestedPref('preferences', 'security', {
              ...p?.preferences?.security,
              biometric_enabled: v,
            })
          }
        />
      </SectionCard>

      <SectionCard title="Notifications">
        {[
          { key: 'email', label: 'Email Notifications', sub: 'Receive updates via email' },
          { key: 'push', label: 'Push Notifications', sub: 'Receive push notifications' },
          { key: 'sms', label: 'SMS Notifications', sub: 'Receive text messages' },
          { key: 'security_alerts', label: 'Security Alerts', sub: 'Account security notifications' },
          { key: 'job_updates', label: 'Job Updates', sub: 'New jobs and assignments' },
          { key: 'team_updates', label: 'Team Updates', sub: 'Team announcements and changes' },
          { key: 'marketing', label: 'Marketing Emails', sub: 'News and promotional content' },
        ].map(n => (
          <ToggleSetting
            key={n.key}
            label={n.label}
            sub={n.sub}
            value={p?.preferences?.notifications?.[n.key]}
            onChange={v =>
              saveNestedPref('preferences', 'notifications', {
                ...p?.preferences?.notifications,
                [n.key]: v,
              })
            }
          />
        ))}
      </SectionCard>

      <SectionCard title="Privacy">
        <View style={{ marginBottom: 14 }}>
          <Text style={[styles.toggleLabel, { marginBottom: 4 }]}>Profile Visibility</Text>
          <Text style={[styles.toggleSub, { marginBottom: 8 }]}>Who can see your profile</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {['public', 'team', 'managers', 'private'].map(o => (
              <TouchableOpacity
                key={o}
                onPress={() =>
                  saveNestedPref('preferences', 'privacy', {
                    ...p?.preferences?.privacy,
                    profile_visibility: o,
                  })
                }
                style={[styles.chip, p?.preferences?.privacy?.profile_visibility === o && styles.chipActive]}>
                <Text
                  style={[
                    styles.chipText,
                    p?.preferences?.privacy?.profile_visibility === o && styles.chipTextActive,
                    { textTransform: 'capitalize' },
                  ]}>
                  {o}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <ToggleSetting
          label="Appear in Search"
          sub="Let others find your profile in directory"
          value={p?.preferences?.privacy?.search_visibility}
          onChange={v =>
            saveNestedPref('preferences', 'privacy', {
              ...p?.preferences?.privacy,
              search_visibility: v,
            })
          }
        />

        <ToggleSetting
          label="Data Sharing"
          sub="Allow anonymised data for analytics"
          value={p?.preferences?.privacy?.data_sharing}
          onChange={v =>
            saveNestedPref('preferences', 'privacy', {
              ...p?.preferences?.privacy,
              data_sharing: v,
            })
          }
        />
      </SectionCard>
    </View>
  );

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView
        style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' }}>
        <ActivityIndicator size="large" color="#0e4e96" />
        <Text style={{ color: '#78909C', marginTop: 14, fontSize: 14 }}>Loading profile…</Text>
      </SafeAreaView>
    );
  }

  if (userNotFound) {
    return (
      <SafeAreaView
        style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' }}>
        <MaterialCommunityIcons name="account-alert" size={64} color="#F57C00" />
        <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#263238', marginTop: 16 }}>
          User Not Found
        </Text>
        <Text
          style={{
            fontSize: 14,
            color: '#78909C',
            textAlign: 'center',
            marginTop: 8,
            marginHorizontal: 32,
          }}>
          We couldn't find your profile. Please check your connection or contact support.
        </Text>
        <TouchableOpacity
          style={{ marginTop: 24, backgroundColor: '#0e4e96', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8 }}
          onPress={() => {
            setLoading(true);
            resolveUserId();
          }}>
          <Text style={{ color: '#fff', fontWeight: '600' }}>Retry</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview',      label: 'Overview',      icon: 'home-outline'      },
    { id: 'personal',      label: 'Personal',       icon: 'account-outline'   },
    { id: 'professional',  label: 'Professional',   icon: 'briefcase-outline' },
    { id: 'experience',    label: 'Experience',     icon: 'timeline-outline'  },
    { id: 'company',       label: 'Company',        icon: 'domain'            },
    { id: 'settings',      label: 'Settings',       icon: 'cog-outline'       },
  ];

  const getStrength = pw => {
    const checks = {
      length: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[^A-Za-z0-9]/.test(pw),
    };
    const score = Object.values(checks).filter(Boolean).length;
    const info =
      score <= 1
        ? { label: 'Very Weak', color: '#D32F2F', bars: 1 }
        : score === 2
        ? { label: 'Weak', color: '#F57C00', bars: 2 }
        : score === 3
        ? { label: 'Fair', color: '#F9A825', bars: 3 }
        : score === 4
        ? { label: 'Strong', color: '#388E3C', bars: 4 }
        : { label: 'Very Strong', color: '#1B5E20', bars: 5 };
    return { checks, score, ...info };
  };
  const strength = getStrength(pwForm.next || '');

  return (
    <SafeAreaView style={styles.root}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton}>
          <Ionicons name="arrow-back" size={22} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{editing ? 'Edit Profile' : 'My Profile'}</Text>
        {editing ? (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              onPress={cancelEdit}
              style={[styles.headerButton, { backgroundColor: '#ECEFF1' }]}>
              <Ionicons name="close" size={20} color="#607D8B" />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={saveProfile}
              disabled={saving}
              style={[styles.headerButton, { backgroundColor: '#0e4e96' }]}>
              {saving ? <ActivityIndicator size={16} color="#fff" /> : <Ionicons name="checkmark" size={20} color="#fff" />}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            onPress={startEdit}
            style={{ backgroundColor: '#E3F2FD', borderRadius: 16, paddingHorizontal: 14, paddingVertical: 7 }}>
            <Text style={{ fontSize: 14, color: '#0e4e96', fontWeight: '700' }}>Edit</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Tabs */}
      <View style={styles.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
          {tabs.map(t => (
            <TouchableOpacity
              key={t.id}
              style={[styles.tab, activeTab === t.id && styles.tabActive]}
              onPress={() => setActiveTab(t.id)}>
              <MaterialCommunityIcons name={t.icon} size={17} color={activeTab === t.id ? '#0e4e96' : '#90A4AE'} />
              <Text style={[styles.tabLabel, activeTab === t.id && styles.tabLabelActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ padding: 14, paddingBottom: 40 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              loadUserProfile().finally(() => setRefreshing(false));
            }}
            colors={['#0e4e96']}
          />
        }>
        {activeTab === 'overview'     && renderOverview()}
        {activeTab === 'personal'     && renderPersonal()}
        {activeTab === 'professional' && renderProfessional()}
        {activeTab === 'experience'   && renderExperience()}
        {activeTab === 'company'      && renderCompanyTab()}
        {activeTab === 'settings'     && renderSettings()}
      </ScrollView>

      {/* Modals */}
    <Modal visible={imgModal} animationType="slide" transparent>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Profile Photo</Text>
            <TouchableOpacity onPress={() => setImgModal(false)}>
              <Ionicons name="close" size={24} color="#607D8B" />
            </TouchableOpacity>
          </View>

          <View style={{ gap: 12, marginTop: 10 }}>
            <TouchableOpacity style={styles.optionButton} onPress={() => pickImage(false)}>
              <MaterialCommunityIcons name="image-multiple" size={24} color="#0e4e96" />
              <Text style={styles.optionText}>Choose from Gallery</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.optionButton} onPress={() => pickImage(true)}>
              <MaterialCommunityIcons name="camera" size={24} color="#0e4e96" />
              <Text style={styles.optionText}>Take a Photo</Text>
            </TouchableOpacity>

            {profile?.profile_image && (
              <TouchableOpacity
                style={[styles.optionButton, { backgroundColor: '#FFEBEE' }]}
                onPress={removeImage}>
                <MaterialCommunityIcons name="delete-outline" size={24} color="#D32F2F" />
                <Text style={[styles.optionText, { color: '#D32F2F' }]}>Remove Photo</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
      <Modal visible={pwModal} animationType="slide">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setPwModal(false)}>
              <Ionicons name="close" size={24} color="#607D8B" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Change Password</Text>
            <View style={{ width: 28 }} />
          </View>

          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={{ flex: 1 }}>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
              <View style={{ marginBottom: 16 }}>
                <Text style={[styles.label, { marginBottom: 6 }]}>Current Password</Text>
                <View style={styles.passwordInput}>
                  <MaterialCommunityIcons name="lock-outline" size={18} color="#90A4AE" />
                  <TextInput
                    style={{ flex: 1, fontSize: 15, color: '#263238', marginLeft: 10 }}
                    value={pwForm.current}
                    onChangeText={v => setPwForm(p => ({ ...p, current: v }))}
                    secureTextEntry={!showPw.current}
                    placeholder="Enter current password"
                    placeholderTextColor="#BDBDBD"
                    autoCapitalize="none"
                  />
                  <TouchableOpacity onPress={() => setShowPw(p => ({ ...p, current: !p.current }))}>
                    <MaterialCommunityIcons
                      name={showPw.current ? 'eye-off' : 'eye'}
                      size={20}
                      color="#90A4AE"
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={{ marginBottom: 16 }}>
                <Text style={[styles.label, { marginBottom: 6 }]}>New Password</Text>
                <View style={styles.passwordInput}>
                  <MaterialCommunityIcons name="lock-outline" size={18} color="#90A4AE" />
                  <TextInput
                    style={{ flex: 1, fontSize: 15, color: '#263238', marginLeft: 10 }}
                    value={pwForm.next}
                    onChangeText={v => setPwForm(p => ({ ...p, next: v }))}
                    secureTextEntry={!showPw.next}
                    placeholder="Enter new password"
                    placeholderTextColor="#BDBDBD"
                    autoCapitalize="none"
                  />
                  <TouchableOpacity onPress={() => setShowPw(p => ({ ...p, next: !p.next }))}>
                    <MaterialCommunityIcons
                      name={showPw.next ? 'eye-off' : 'eye'}
                      size={20}
                      color="#90A4AE"
                    />
                  </TouchableOpacity>
                </View>

                {pwForm.next.length > 0 && (
                  <View style={{ marginTop: 8 }}>
                    <View style={{ flexDirection: 'row', gap: 3, marginBottom: 5 }}>
                      {[1, 2, 3, 4, 5].map(i => (
                        <View
                          key={i}
                          style={{
                            flex: 1,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: i <= strength.bars ? strength.color : '#E0E0E0',
                          }}
                        />
                      ))}
                    </View>
                    <Text style={{ fontSize: 12, color: strength.color, fontWeight: '700' }}>
                      {strength.label}
                    </Text>
                  </View>
                )}
              </View>

              <View style={{ marginBottom: 24 }}>
                <Text style={[styles.label, { marginBottom: 6 }]}>Confirm Password</Text>
                <View style={styles.passwordInput}>
                  <MaterialCommunityIcons name="lock-outline" size={18} color="#90A4AE" />
                  <TextInput
                    style={{ flex: 1, fontSize: 15, color: '#263238', marginLeft: 10 }}
                    value={pwForm.confirm}
                    onChangeText={v => setPwForm(p => ({ ...p, confirm: v }))}
                    secureTextEntry={!showPw.confirm}
                    placeholder="Confirm new password"
                    placeholderTextColor="#BDBDBD"
                    autoCapitalize="none"
                  />
                  <TouchableOpacity onPress={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))}>
                    <MaterialCommunityIcons
                      name={showPw.confirm ? 'eye-off' : 'eye'}
                      size={20}
                      color="#90A4AE"
                    />
                  </TouchableOpacity>
                </View>

                {pwForm.confirm && pwForm.next && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 5 }}>
                    <Ionicons
                      name={pwForm.confirm === pwForm.next ? 'checkmark-circle' : 'close-circle'}
                      size={14}
                      color={pwForm.confirm === pwForm.next ? '#388E3C' : '#D32F2F'}
                    />
                    <Text
                      style={{
                        fontSize: 12,
                        color: pwForm.confirm === pwForm.next ? '#388E3C' : '#D32F2F',
                        fontWeight: '600',
                      }}>
                      <Text>{pwForm.confirm === pwForm.next ? 'Passwords match' : 'Passwords do not match'}</Text>
                    </Text>
                  </View>
                )}
              </View>

              <TouchableOpacity
                style={[styles.saveButton, changingPw && styles.saveButtonDisabled]}
                onPress={changePassword}
                disabled={changingPw}>
                {changingPw ? <ActivityIndicator color="#fff" size={18} /> : <Text style={styles.saveButtonText}>Change Password</Text>}
              </TouchableOpacity>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    <Modal visible={skillModal} animationType="slide">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setSkillModal(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>{editSkill ? 'Edit Skill' : 'Add Skill'}</Text>
          <View style={{ width: 28 }} />
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
          <Text style={styles.label}>Skill Name *</Text>
          <TextInput
            style={[styles.input, { marginTop: 6, marginBottom: 16 }]}
            value={skillForm.name}
            onChangeText={v => setSkillForm(p => ({ ...p, name: v }))}
            placeholder="e.g. Project Management"
            placeholderTextColor="#BDBDBD"
            autoFocus
          />

          <Text style={[styles.label, { marginBottom: 10 }]}>Proficiency Level</Text>
          <View style={{ gap: 8, marginBottom: 24 }}>
            {['Beginner', 'Intermediate', 'Advanced', 'Expert'].map((level, i) => (
              <TouchableOpacity
                key={level}
                onPress={() => setSkillForm(p => ({ ...p, proficiency: i + 1 }))}
                style={[
                  styles.proficiencyRow,
                  skillForm.proficiency === i + 1 && {
                    borderColor: getProficiencyColor(i + 1),
                    backgroundColor: getProficiencyColor(i + 1) + '12',
                  },
                ]}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#263238' }}>{level}</Text>
                  <View
                    style={{
                      height: 4,
                      backgroundColor: '#ECEFF1',
                      borderRadius: 2,
                      marginTop: 4,
                      overflow: 'hidden',
                    }}>
                    <View
                      style={{
                        width: `${((i + 1) / 4) * 100}%`,
                        height: '100%',
                        backgroundColor: getProficiencyColor(i + 1),
                        borderRadius: 2,
                      }}
                    />
                  </View>
                </View>
                <Ionicons
                  name={skillForm.proficiency === i + 1 ? 'radio-button-on' : 'radio-button-off'}
                  size={22}
                  color={skillForm.proficiency === i + 1 ? getProficiencyColor(i + 1) : '#B0BEC5'}
                />
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity style={styles.saveButton} onPress={saveSkill}>
            <Text style={styles.saveButtonText}>{editSkill ? 'Update Skill' : 'Add Skill'}</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
    <Modal visible={certModal} animationType="slide">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setCertModal(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>{editCert ? 'Edit Certification' : 'Add Certification'}</Text>
          <View style={{ width: 28 }} />
        </View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Certification Name *</Text>
              <TextInput
                style={styles.input}
                value={certForm.name}
                onChangeText={v => setCertForm(p => ({ ...p, name: v }))}
                placeholder="e.g. PMP, PRINCE2"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Issuing Organization</Text>
              <TextInput
                style={styles.input}
                value={certForm.issuer}
                onChangeText={v => setCertForm(p => ({ ...p, issuer: v }))}
                placeholder="e.g. PMI, AXELOS"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Issue Date</Text>
              <TextInput
                style={styles.input}
                value={certForm.issue_date}
                onChangeText={v => setCertForm(p => ({ ...p, issue_date: v }))}
                placeholder="YYYY-MM-DD"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 24 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Expiry Date</Text>
              <TextInput
                style={styles.input}
                value={certForm.expiry_date}
                onChangeText={v => setCertForm(p => ({ ...p, expiry_date: v }))}
                placeholder="YYYY-MM-DD (leave blank if no expiry)"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <TouchableOpacity style={styles.saveButton} onPress={saveCert}>
              <Text style={styles.saveButtonText}>{editCert ? 'Update' : 'Add Certification'}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
    <Modal visible={eduModal} animationType="slide">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setEduModal(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>{editEdu ? 'Edit Education' : 'Add Education'}</Text>
          <View style={{ width: 28 }} />
        </View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Institution *</Text>
              <TextInput
                style={styles.input}
                value={eduForm.institution}
                onChangeText={v => setEduForm(p => ({ ...p, institution: v }))}
                placeholder="University name"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Degree</Text>
              <TextInput
                style={styles.input}
                value={eduForm.degree}
                onChangeText={v => setEduForm(p => ({ ...p, degree: v }))}
                placeholder="e.g. Bachelor of Science"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Field of Study</Text>
              <TextInput
                style={styles.input}
                value={eduForm.field}
                onChangeText={v => setEduForm(p => ({ ...p, field: v }))}
                placeholder="e.g. Civil Engineering"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Start Year</Text>
              <TextInput
                style={styles.input}
                value={eduForm.start_year}
                onChangeText={v => setEduForm(p => ({ ...p, start_year: v }))}
                placeholder="e.g. 2010"
                keyboardType="numeric"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>End Year</Text>
              <TextInput
                style={styles.input}
                value={eduForm.end_year}
                onChangeText={v => setEduForm(p => ({ ...p, end_year: v }))}
                placeholder="e.g. 2014 (leave blank if current)"
                keyboardType="numeric"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 24 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Grade / Result</Text>
              <TextInput
                style={styles.input}
                value={eduForm.grade}
                onChangeText={v => setEduForm(p => ({ ...p, grade: v }))}
                placeholder="e.g. 2:1, First Class"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <TouchableOpacity style={styles.saveButton} onPress={saveEdu}>
              <Text style={styles.saveButtonText}>{editEdu ? 'Update' : 'Add Education'}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
    <Modal visible={expModal} animationType="slide">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setExpModal(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>{editExp ? 'Edit Experience' : 'Add Experience'}</Text>
          <View style={{ width: 28 }} />
        </View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18 }}>
            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Company *</Text>
              <TextInput
                style={styles.input}
                value={expForm.company}
                onChangeText={v => setExpForm(p => ({ ...p, company: v }))}
                placeholder="Company name"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Job Title *</Text>
              <TextInput
                style={styles.input}
                value={expForm.role}
                onChangeText={v => setExpForm(p => ({ ...p, role: v }))}
                placeholder="e.g. Senior Engineer"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Start Date</Text>
              <TextInput
                style={styles.input}
                value={expForm.start_date}
                onChangeText={v => setExpForm(p => ({ ...p, start_date: v }))}
                placeholder="YYYY-MM-DD"
                placeholderTextColor="#BDBDBD"
              />
            </View>

            <View style={{ marginBottom: 14 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={[styles.label, { marginBottom: 6 }]}>I currently work here</Text>
                <Switch
                  value={expForm.current}
                  onValueChange={v => setExpForm(p => ({ ...p, current: v, end_date: v ? '' : p.end_date }))}
                  trackColor={{ false: '#CFD8DC', true: '#0e4e96' }}
                  thumbColor="#fff"
                />
              </View>
            </View>

            {!expForm.current && (
              <View style={{ marginBottom: 14 }}>
                <Text style={[styles.label, { marginBottom: 6 }]}>End Date</Text>
                <TextInput
                  style={styles.input}
                  value={expForm.end_date}
                  onChangeText={v => setExpForm(p => ({ ...p, end_date: v }))}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor="#BDBDBD"
                />
              </View>
            )}

            <View style={{ marginBottom: 24 }}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Description</Text>
              <TextInput
                style={[styles.input, { height: 90, textAlignVertical: 'top', paddingTop: 10 }]}
                value={expForm.description}
                onChangeText={v => setExpForm(p => ({ ...p, description: v }))}
                placeholder="Key responsibilities and achievements..."
                placeholderTextColor="#BDBDBD"
                multiline
                numberOfLines={4}
              />
            </View>

            <TouchableOpacity style={styles.saveButton} onPress={saveExp}>
              <Text style={styles.saveButtonText}>{editExp ? 'Update' : 'Add Experience'}</Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
    </SafeAreaView>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F5F7FA',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#ECEFF1',
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#263238',
  },
  headerButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#F5F7FA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabBar: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#ECEFF1',
    paddingVertical: 4,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 9,
    marginHorizontal: 2,
    borderRadius: 20,
    gap: 5,
  },
  tabActive: {
    backgroundColor: '#E3F2FD',
  },
  tabLabel: {
    fontSize: 12,
    color: '#90A4AE',
    fontWeight: '600',
  },
  tabLabelActive: {
    color: '#0e4e96',
    fontWeight: '800',
  },
  heroCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 22,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 8,
    elevation: 3,
  },
  heroName: {
    fontSize: 22,
    fontWeight: '900',
    color: '#263238',
    marginTop: 12,
    textAlign: 'center',
  },
  heroRole: {
    fontSize: 14,
    color: '#78909C',
    marginTop: 3,
    textAlign: 'center',
  },
  heroBadge: {
    backgroundColor: '#E3F2FD',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#BBDEFB',
  },
  heroBadgeText: {
    fontSize: 11,
    color: '#0e4e96',
    fontWeight: '700',
  },
  statBox: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    alignItems: 'center',
    gap: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  statNum: {
    fontSize: 20,
    fontWeight: '900',
  },
  statLabel: {
    fontSize: 10,
    color: '#90A4AE',
    fontWeight: '600',
  },
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 5,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#263238',
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
    color: '#546E7A',
  },
  input: {
    backgroundColor: '#F5F7FA',
    borderWidth: 1.5,
    borderColor: '#CFD8DC',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    color: '#263238',
  },
  readValue: {
    fontSize: 14,
    color: '#37474F',
    fontWeight: '500',
    paddingVertical: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F5F7FA',
    gap: 12,
  },
  rowIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowLabel: {
    fontSize: 12,
    color: '#90A4AE',
    fontWeight: '600',
  },
  rowValue: {
    fontSize: 14,
    color: '#263238',
    fontWeight: '500',
    marginTop: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F5F7FA',
  },
  toggleLabel: {
    fontSize: 14,
    color: '#263238',
    fontWeight: '600',
  },
  toggleSub: {
    fontSize: 12,
    color: '#78909C',
    marginTop: 2,
  },
  iconButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E3F2FD',
    alignItems: 'center',
    justifyContent: 'center',
  },
  skillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F5F7FA',
    gap: 12,
  },
  certRow: {
    backgroundColor: '#F5F7FA',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#0e4e96',
    gap: 4,
  },
  expiredBadge: {
    backgroundColor: '#FFEBEE',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  timelineItem: {
    flexDirection: 'row',
    paddingLeft: 20,
    borderLeftWidth: 2,
    borderLeftColor: '#BBDEFB',
    marginLeft: 6,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#0e4e96',
    position: 'absolute',
    left: -7,
    top: 4,
  },
  timelineLine: {
    position: 'absolute',
    left: -1,
    top: 18,
    bottom: 0,
    width: 2,
    backgroundColor: '#BBDEFB',
  },
  emptyText: {
    fontSize: 13,
    color: '#B0BEC5',
    textAlign: 'center',
    paddingVertical: 16,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F5F7FA',
    borderWidth: 1,
    borderColor: '#CFD8DC',
  },
  chipActive: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  chipText: {
    fontSize: 12,
    color: '#546E7A',
    fontWeight: '600',
  },
  chipTextActive: {
    color: '#fff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    minHeight: 200,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#ECEFF1',
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#263238',
  },
  saveButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#fff',
  },
  passwordInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F7FA',
    borderWidth: 1.5,
    borderColor: '#CFD8DC',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  proficiencyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#CFD8DC',
    gap: 12,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F7FA',
    borderRadius: 14,
    padding: 18,
    gap: 14,
    borderWidth: 1,
    borderColor: '#CFD8DC',
  },
  optionText: {
    fontSize: 15,
    color: '#263238',
    fontWeight: '700',
  },
  companyBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    marginBottom: 10,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#BBDEFB',
  },
});